import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../../../context/AuthContext';
import api from '../../../services/api';
import PageHeader from '../../../components/common/PageHeader';
import StatCard from '../../../components/common/StatCard';
import AlertToast from '../../../components/common/AlertToast';
import CourseNameBadge from '../../../components/common/CourseNameBadge';
import ScholarshipQuizPrompt from '../../../components/quiz/ScholarshipQuizPrompt';
import Editor from '@monaco-editor/react';
import './Assessments.student.css';

// ─── Constants ─────────────────────────────────────────────────────────────────

const ASSESSMENT_TYPES = [
  { value: 'quiz',       label: 'Quiz'       },
  { value: 'mixed',      label: 'Mixed'      },
  { value: 'practical',  label: 'Practical'  },
  { value: 'task_based', label: 'Task-Based' },
  { value: 'viva',       label: 'Viva'       },
  { value: 'attendance_based', label: 'Attendance-Based' },
];

const DIFFICULTIES = [
  { value: 'easy',   label: 'Easy' },
  { value: 'medium', label: 'Medium' },
  { value: 'hard',   label: 'Hard' },
  { value: 'mixed',  label: 'Mixed' },
];

const Q_TYPES = [
  { value: 'mcq_single',   label: 'MCQ (Single Correct)'   },
  { value: 'mcq_multi',    label: 'MCQ (Multiple Correct)' },
  { value: 'true_false',   label: 'True / False'           },
  { value: 'short_answer', label: 'Short Answer'           },
  { value: 'long_answer',  label: 'Long Answer'            },
  { value: 'practical',    label: 'Practical'              },
  { value: 'coding',       label: 'Coding'                 },
];

const CODE_LANGUAGES = [
  { value: 'javascript', label: 'JavaScript', monaco: 'javascript', extension: 'js' },
  { value: 'python',    label: 'Python',     monaco: 'python',     extension: 'py' },
  { value: 'java',      label: 'Java',       monaco: 'java',       extension: 'java' },
  { value: 'cpp',       label: 'C++',        monaco: 'cpp',        extension: 'cpp' },
  { value: 'c',         label: 'C',          monaco: 'c',          extension: 'c' },
  { value: 'csharp',    label: 'C#',         monaco: 'csharp',     extension: 'cs' },
  { value: 'ruby',      label: 'Ruby',       monaco: 'ruby',       extension: 'rb' },
  { value: 'go',        label: 'Go',         monaco: 'go',         extension: 'go' },
  { value: 'rust',      label: 'Rust',       monaco: 'rust',       extension: 'rs' },
  { value: 'php',       label: 'PHP',        monaco: 'php',        extension: 'php' },
];

const DEFAULT_TEMPLATES = {
  javascript: '// Write your JavaScript code here\n\nconsole.log("Hello, World!");',
  python:     '# Write your Python code here\n\nprint("Hello, World!")',
  java:       '// Write your Java code here\npublic class Main {\n    public static void main(String[] args) {\n        System.out.println("Hello, World!");\n    }\n}',
  cpp:        '// Write your C++ code here\n#include <iostream>\nusing namespace std;\n\nint main() {\n    cout << "Hello, World!" << endl;\n    return 0;\n}',
  c:          '// Write your C code here\n#include <stdio.h>\nint main() {\n    printf("Hello, World!\\n");\n    return 0;\n}',
  csharp:     '// Write your C# code here\nusing System;\nclass Program {\n    static void Main() {\n        Console.WriteLine("Hello, World!");\n    }\n}',
  ruby:       '# Write your Ruby code here\nputs "Hello, World!"',
  go:         '// Write your Go code here\npackage main\nimport "fmt"\nfunc main() {\n    fmt.Println("Hello, World!")\n}',
  rust:       '// Write your Rust code here\nfn main() {\n    println!("Hello, World!");\n}',
  php:        '<?php\n// Write your PHP code here\necho "Hello, World!";\n?>',
};

const STATUS_COLOR = { draft:'secondary', active:'success', closed:'warning', archived:'dark' };
const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-IN', { day:'2-digit', month:'short', year:'numeric' }) : '—';
const fmtDateTime = (d) => d ? new Date(d).toLocaleString('en-IN') : '—';

const ATTEMPT_MODES = [
  { value: 'open_book',  label: 'Open Book',  color: 'success' },
  { value: 'restricted', label: 'Restricted', color: 'warning' },
  { value: 'strict',     label: 'Strict',     color: 'danger'  },
];

// ─── BSModal ────────────────────────────────────────────────────────────────────

const BSModal = ({ show, onClose, title, size = '', children, footer }) =>
  show ? (
    <div className="modal fade show d-block" style={{ background:'rgba(0,0,0,0.45)', zIndex:1050 }}
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div className={`modal-dialog modal-dialog-scrollable modal-dialog-centered ${size}`}>
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">{title}</h5>
            <button className="btn-close" onClick={onClose} />
          </div>
          <div className="modal-body">{children}</div>
          {footer && <div className="modal-footer">{footer}</div>}
        </div>
      </div>
    </div>
  ) : null;

// ─── Question Editor ─────────────────────────────────────────────────────────

const blankQ = {
  question: '', type: 'mcq_single', marks: 1,
  options: [
    { text: '', isCorrect: true },
    { text: '', isCorrect: false },
    { text: '', isCorrect: false },
    { text: '', isCorrect: false },
  ],
  expectedAnswer: '', explanation: '',
  difficulty: 'medium', topic: '', tags: [],
};

const needsOptions = (t) => ['mcq_single', 'mcq_multi', 'true_false'].includes(t);
const needsExpected = (t) => ['short_answer', 'long_answer'].includes(t);

const QuestionEditor = ({ questions, onChange, onOpenBank, onOpenAI, onOpenUpload, disabled }) => {
  const [editIdx, setEditIdx] = useState(null);
  const [editQ, setEditQ]     = useState(null);

  const openNew  = () => {
    setEditQ({ ...blankQ, options: blankQ.options.map(o => ({ ...o })) });
    setEditIdx(-1);
  };
  const openEdit = (i) => {
    const src = questions[i] || {};
    setEditQ({
      question: src.question || src.questionText || '',
      type: src.type || 'mcq_single',
      marks: src.marks ?? 1,
      options: (src.options || []).map(o =>
        typeof o === 'string' ? { text: o, isCorrect: false } : { text: o.text, isCorrect: !!o.isCorrect }
      ),
      expectedAnswer: src.expectedAnswer || src.correctAnswer || '',
      explanation: src.explanation || '',
      difficulty: src.difficulty || 'medium',
      topic: src.topic || '',
      tags: src.tags || [],
      sourceBank: src.sourceBank,
    });
    setEditIdx(i);
  };

  const handleTypeChange = (t) => {
    setEditQ(p => {
      const next = { ...p, type: t };
      if (t === 'true_false') {
        next.options = [
          { text: 'True', isCorrect: !!p.options?.[0]?.isCorrect },
          { text: 'False', isCorrect: !!p.options?.[1]?.isCorrect },
        ];
      } else if (needsOptions(t) && (!p.options || p.options.length < 2)) {
        next.options = blankQ.options.map(o => ({ ...o }));
      }
      // Ensure only one correct for single-select
      if (t === 'mcq_single') {
        let firstCorrectSeen = false;
        next.options = (next.options || []).map(o => {
          if (o.isCorrect && !firstCorrectSeen) { firstCorrectSeen = true; return o; }
          return { ...o, isCorrect: false };
        });
      }
      return next;
    });
  };

  const saveQ = () => {
    if (!editQ.question.trim()) return;
    if (needsOptions(editQ.type)) {
      if ((editQ.options || []).filter(o => o.isCorrect).length === 0) return;
    }
    const updated = [...questions];
    if (editIdx === -1) updated.push({ ...editQ });
    else updated[editIdx] = { ...editQ };
    onChange(updated);
    setEditIdx(null); setEditQ(null);
  };

  const deleteQ = (i) => { const updated = [...questions]; updated.splice(i, 1); onChange(updated); };
  const setOpt  = (i, k, v) => { const opts = editQ.options.map((o,j) => j===i ? {...o,[k]:v} : o); setEditQ(p=>({...p,options:opts})); };
  const setSingleCorrect = (i) => { const opts = editQ.options.map((o,j) => ({...o,isCorrect:j===i})); setEditQ(p=>({...p,options:opts})); };
  const toggleMultiCorrect = (i) => { const opts = editQ.options.map((o,j) => j===i ? {...o,isCorrect:!o.isCorrect} : o); setEditQ(p=>({...p,options:opts})); };
  const addOpt  = () => setEditQ(p=>({...p,options:[...p.options,{text:'',isCorrect:false}]}));
  const removeOpt=(i)=> { const opts=editQ.options.filter((_,j)=>j!==i); setEditQ(p=>({...p,options:opts})); };

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-2">
        <span className="fw-semibold small">Questions ({questions.length})
          {' '}<span className="text-muted">· Total marks {questions.reduce((s,q)=>s+(Number(q.marks)||0),0)}</span>
        </span>
        {!disabled && (
          <div className="d-flex gap-1 flex-wrap">
            {onOpenAI && (
              <button className="btn btn-outline-info btn-sm" onClick={onOpenAI} type="button">
                <i className="bi bi-stars me-1" />AI Generate
              </button>
            )}
            {onOpenBank && (
              <button className="btn btn-outline-success btn-sm" onClick={onOpenBank} type="button">
                <i className="bi bi-collection me-1" />From Question Bank
              </button>
            )}
            {onOpenUpload && (
              <button className="btn btn-outline-warning btn-sm" onClick={onOpenUpload} type="button">
                <i className="bi bi-file-earmark-arrow-up me-1" />Upload PDF / Word
              </button>
            )}
            <button className="btn btn-outline-primary btn-sm" onClick={openNew} type="button">
              <i className="bi bi-plus-lg me-1" />Add Manually
            </button>
          </div>
        )}
      </div>

      {questions.length === 0 && <div className="text-muted small">No questions yet. Add manually or import from the question bank.</div>}

      {questions.map((q, i) => (
        <div key={i} className="border rounded p-2 mb-2 bg-light">
          <div className="d-flex justify-content-between align-items-start">
            <div className="small" style={{ flex: 1 }}>
              <span className="badge bg-secondary me-1">{Q_TYPES.find(t=>t.value===q.type)?.label||q.type}</span>
              {q.difficulty && <span className="badge bg-light text-dark border me-1">{q.difficulty}</span>}
              {q.sourceBank && <span className="badge bg-info-subtle text-info border me-1" title="Imported from question bank"><i className="bi bi-link-45deg" /></span>}
              <span className="fw-semibold">{q.question || q.questionText}</span>
              <span className="text-muted ms-2">({q.marks} mark{q.marks!==1?'s':''})</span>
            </div>
            {!disabled && (
              <div className="d-flex gap-1">
                <button className="btn btn-outline-primary btn-sm" onClick={() => openEdit(i)} type="button"><i className="bi bi-pencil" /></button>
                <button className="btn btn-outline-danger btn-sm" onClick={() => deleteQ(i)} type="button"><i className="bi bi-trash" /></button>
              </div>
            )}
          </div>
          {needsOptions(q.type) && (
            <div className="mt-1 ms-2">
              {(q.options||[]).map((o,j) => {
                const text = typeof o === 'string' ? o : o.text;
                const isCorrect = typeof o === 'object' && o.isCorrect;
                return (
                  <div key={j} className="small">
                    {isCorrect ? <i className="bi bi-check-circle-fill text-success me-1" /> : <i className="bi bi-circle me-1 text-muted" />}
                    {text}
                  </div>
                );
              })}
            </div>
          )}
          {needsExpected(q.type) && q.expectedAnswer && (
            <div className="mt-1 ms-2 small text-muted"><em>Expected: {q.expectedAnswer}</em></div>
          )}
        </div>
      ))}

      {/* Question Edit Modal */}
      <BSModal
        show={editIdx !== null}
        onClose={() => { setEditIdx(null); setEditQ(null); }}
        title={editIdx === -1 ? 'Add Question' : 'Edit Question'}
        size="modal-lg"
        footer={
          <>
            <button className="btn btn-secondary" onClick={() => { setEditIdx(null); setEditQ(null); }}>Cancel</button>
            <button className="btn btn-primary" onClick={saveQ}>Save Question</button>
          </>
        }
      >
        {editQ && (
          <div className="row g-3">
            <div className="col-12">
              <label className="form-label">Question Text <span className="text-danger">*</span></label>
              <textarea className="form-control" rows={3} value={editQ.question}
                        onChange={e => setEditQ(p=>({...p,question:e.target.value}))} />
            </div>
            <div className="col-md-5">
              <label className="form-label">Type</label>
              <select className="form-select" value={editQ.type} onChange={e => handleTypeChange(e.target.value)}>
                {Q_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
            </div>
            <div className="col-md-3">
              <label className="form-label">Difficulty</label>
              <select className="form-select" value={editQ.difficulty || 'medium'}
                      onChange={e => setEditQ(p=>({...p,difficulty:e.target.value}))}>
                <option value="easy">Easy</option>
                <option value="medium">Medium</option>
                <option value="hard">Hard</option>
              </select>
            </div>
            <div className="col-md-2">
              <label className="form-label">Marks</label>
              <input className="form-control" type="number" min="0.5" step="0.5" value={editQ.marks}
                     onChange={e => setEditQ(p=>({...p,marks:e.target.value}))} />
            </div>
            <div className="col-md-2">
              <label className="form-label small">Topic</label>
              <input className="form-control form-control-sm" value={editQ.topic || ''}
                     onChange={e => setEditQ(p=>({...p,topic:e.target.value}))} placeholder="optional" />
            </div>

            {editQ.type === 'mcq_single' && (
              <div className="col-12">
                <label className="form-label">Options <span className="text-muted small">(select ONE correct option)</span></label>
                {editQ.options.map((o, i) => (
                  <div key={i} className="input-group mb-2">
                    <div className="input-group-text">
                      <input type="radio" name="correctOpt" checked={!!o.isCorrect} onChange={() => setSingleCorrect(i)} />
                    </div>
                    <input className="form-control" value={o.text} onChange={e => setOpt(i, 'text', e.target.value)} placeholder={`Option ${i+1}`} />
                    {editQ.options.length > 2 && (
                      <button className="btn btn-outline-danger" type="button" onClick={() => removeOpt(i)}><i className="bi bi-x" /></button>
                    )}
                  </div>
                ))}
                <button className="btn btn-outline-secondary btn-sm" type="button" onClick={addOpt}><i className="bi bi-plus me-1" />Add Option</button>
              </div>
            )}

            {editQ.type === 'mcq_multi' && (
              <div className="col-12">
                <label className="form-label">Options <span className="text-muted small">(tick ALL correct options)</span></label>
                {editQ.options.map((o, i) => (
                  <div key={i} className="input-group mb-2">
                    <div className="input-group-text">
                      <input type="checkbox" checked={!!o.isCorrect} onChange={() => toggleMultiCorrect(i)} />
                    </div>
                    <input className="form-control" value={o.text} onChange={e => setOpt(i, 'text', e.target.value)} placeholder={`Option ${i+1}`} />
                    {editQ.options.length > 2 && (
                      <button className="btn btn-outline-danger" type="button" onClick={() => removeOpt(i)}><i className="bi bi-x" /></button>
                    )}
                  </div>
                ))}
                <button className="btn btn-outline-secondary btn-sm" type="button" onClick={addOpt}><i className="bi bi-plus me-1" />Add Option</button>
              </div>
            )}

            {editQ.type === 'true_false' && (
              <div className="col-12">
                <label className="form-label">Correct answer</label>
                <div>
                  <div className="form-check form-check-inline">
                    <input className="form-check-input" type="radio" name="tfCorrect" checked={!!editQ.options[0]?.isCorrect}
                           onChange={() => setSingleCorrect(0)} id="tf-true" />
                    <label className="form-check-label" htmlFor="tf-true">True</label>
                  </div>
                  <div className="form-check form-check-inline">
                    <input className="form-check-input" type="radio" name="tfCorrect" checked={!!editQ.options[1]?.isCorrect}
                           onChange={() => setSingleCorrect(1)} id="tf-false" />
                    <label className="form-check-label" htmlFor="tf-false">False</label>
                  </div>
                </div>
              </div>
            )}

            {needsExpected(editQ.type) && (
              <div className="col-12">
                <label className="form-label">Expected / Reference Answer <span className="text-muted small">(optional — used for auto-scoring short answers)</span></label>
                <textarea className="form-control" rows={editQ.type === 'long_answer' ? 4 : 2} value={editQ.expectedAnswer}
                          onChange={e => setEditQ(p=>({...p,expectedAnswer:e.target.value}))} />
              </div>
            )}

            {editQ.type === 'coding' && (
              <div className="col-12">
                <label className="form-label">Sample Solution</label>
                <textarea className="form-control font-monospace small" rows={4} value={editQ.expectedAnswer}
                          onChange={e => setEditQ(p=>({...p,expectedAnswer:e.target.value}))} />
              </div>
            )}

            {editQ.type === 'coding' && (
              <div className="col-12">
                <div className="form-check">
                  <input className="form-check-input" type="checkbox" id="enableCompiler"
                    checked={!!editQ.enableCompiler}
                    onChange={e => setEditQ(p=>({...p, enableCompiler: e.target.checked }))} />
                  <label className="form-check-label" htmlFor="enableCompiler">
                    <strong>Enable Compiler</strong>
                    <span className="text-muted small"> — student gets a code editor with language selector and run button</span>
                  </label>
                </div>
              </div>
            )}

            <div className="col-12">
              <label className="form-label small">Explanation <span className="text-muted">(shown on review)</span></label>
              <textarea className="form-control form-control-sm" rows={2} value={editQ.explanation || ''}
                        onChange={e => setEditQ(p=>({...p,explanation:e.target.value}))} />
            </div>
          </div>
        )}
      </BSModal>
    </div>
  );
};

// ─── Upload Questions Dialog ─────────────────────────────────────────────────

const CHATGPT_PROMPT = `Generate 10 multiple choice questions on the topic: [YOUR TOPIC].

Use this exact format for every question:

Q1. <Question text>
A) <Option A>
B) <Option B>
C) <Option C>
D) <Option D>
Answer: <A, B, C, or D>
Marks: 1
Difficulty: medium
Explanation: <Brief explanation>

Q2. <Next question>
...

Rules:
- Separate each question with a blank line
- Answer must be a single letter (A, B, C, or D)
- Difficulty must be: easy, medium, or hard
- Do not add any extra text before Q1 or after the last question`;

const UploadQuestionsDialog = ({ show, onClose, onAdd, onAlert }) => {
  const [file, setFile]         = useState(null);
  const [parsing, setParsing]   = useState(false);
  const [parsed, setParsed]     = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [showPrompt, setShowPrompt] = useState(false);
  const fileRef = React.useRef();

  useEffect(() => {
    if (show) { setFile(null); setParsed([]); setSelected(new Set()); setShowPrompt(false); }
  }, [show]);

  const handleFile = (f) => {
    if (!f) return;
    const ok = f.type === 'application/pdf' ||
               f.type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' ||
               f.type === 'application/msword' ||
               f.name.match(/\.(pdf|docx|doc)$/i);
    if (!ok) { onAlert('Only PDF or Word (.docx / .doc) files are supported.', 'warning'); return; }
    setFile(f); setParsed([]); setSelected(new Set());
  };

  const handleParse = async () => {
    if (!file) return;
    setParsing(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const r = await api.post(
        '/training/assessments/parse-questions', fd,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      const qs = r.data?.questions || [];
      if (!qs.length) { onAlert('No questions found in the file. Check the format.', 'warning'); }
      else {
        setParsed(qs);
        setSelected(new Set(qs.map((_, i) => i)));
      }
    } catch (e) {
      onAlert(e.response?.data?.message || 'Failed to parse file.', 'danger');
    }
    setParsing(false);
  };

  const toggleAll = () => {
    if (selected.size === parsed.length) setSelected(new Set());
    else setSelected(new Set(parsed.map((_, i) => i)));
  };
  const toggleRow = (i) => {
    const s = new Set(selected);
    if (s.has(i)) s.delete(i); else s.add(i);
    setSelected(s);
  };

  const handleAdd = () => {
    const toAdd = parsed.filter((_, i) => selected.has(i));
    if (!toAdd.length) { onAlert('Select at least one question.', 'warning'); return; }
    onAdd(toAdd);
    onClose();
  };

  return (
    <BSModal show={show} onClose={onClose} title="Upload Questions from PDF / Word" size="modal-xl"
      footer={parsed.length > 0 ? (
        <>
          <span className="text-muted small me-auto">{selected.size} of {parsed.length} selected</span>
          <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={handleAdd} disabled={selected.size === 0}>
            <i className="bi bi-plus-lg me-1" />Add {selected.size} Question{selected.size !== 1 ? 's' : ''}
          </button>
        </>
      ) : null}
    >
      {/* ChatGPT prompt guide */}
      <div className="mb-3">
        <button className="btn btn-outline-secondary btn-sm w-100 d-flex justify-content-between align-items-center"
                type="button" onClick={() => setShowPrompt(p => !p)}>
          <span><i className="bi bi-chat-dots me-2" />How to generate questions with ChatGPT</span>
          <i className={`bi bi-chevron-${showPrompt ? 'up' : 'down'}`} />
        </button>
        {showPrompt && (
          <div className="border rounded-bottom p-3 bg-light">
            <p className="small text-muted mb-2">
              Copy the prompt below, paste it into ChatGPT, replace <code>[YOUR TOPIC]</code>, then download the response as a <strong>.docx</strong> file (File → Download → Word) or copy it into a Word doc and save as <strong>.pdf</strong>.
            </p>
            <pre className="bg-white border rounded p-2 small" style={{ whiteSpace: 'pre-wrap', maxHeight: 300, overflowY: 'auto', fontSize: 12 }}>
              {CHATGPT_PROMPT}
            </pre>
            <button className="btn btn-sm btn-outline-primary mt-2"
                    onClick={() => { navigator.clipboard.writeText(CHATGPT_PROMPT); onAlert('Prompt copied to clipboard!', 'success'); }}>
              <i className="bi bi-clipboard me-1" />Copy Prompt
            </button>
          </div>
        )}
      </div>

      {/* File picker */}
      <div className="mb-3">
        <label className="form-label fw-semibold">Select File</label>
        <div className="input-group">
          <input type="file" className="form-control" accept=".pdf,.doc,.docx"
                 ref={fileRef}
                 onChange={e => handleFile(e.target.files?.[0])} />
          <button className="btn btn-primary" onClick={handleParse} disabled={!file || parsing}>
            {parsing
              ? <><span className="spinner-border spinner-border-sm me-1" />Parsing…</>
              : <><i className="bi bi-play-fill me-1" />Parse</>}
          </button>
        </div>
        <div className="form-text">Supported: PDF, Word (.docx), Word 97-2003 (.doc)</div>
      </div>

      {/* Parsed questions preview */}
      {parsed.length > 0 && (
        <div>
          <div className="d-flex justify-content-between align-items-center mb-2">
            <span className="fw-semibold small">{parsed.length} question{parsed.length !== 1 ? 's' : ''} found</span>
            <button className="btn btn-outline-secondary btn-sm" onClick={toggleAll}>
              {selected.size === parsed.length ? 'Deselect All' : 'Select All'}
            </button>
          </div>
          <div style={{ maxHeight: 420, overflowY: 'auto' }}>
            <table className="table table-sm table-hover align-middle">
              <thead className="table-light sticky-top">
                <tr>
                  <th style={{ width: 36 }}>
                    <input type="checkbox"
                           checked={selected.size === parsed.length && parsed.length > 0}
                           ref={el => { if (el) el.indeterminate = selected.size > 0 && selected.size < parsed.length; }}
                           onChange={toggleAll} />
                  </th>
                  <th>#</th>
                  <th>Question</th>
                  <th style={{ width: 110 }}>Type</th>
                  <th style={{ width: 90 }}>Difficulty</th>
                  <th style={{ width: 60 }}>Marks</th>
                </tr>
              </thead>
              <tbody>
                {parsed.map((q, i) => (
                  <tr key={i} className={selected.has(i) ? 'table-primary' : ''} style={{ cursor: 'pointer' }} onClick={() => toggleRow(i)}>
                    <td><input type="checkbox" checked={selected.has(i)} onChange={() => toggleRow(i)} onClick={e => e.stopPropagation()} /></td>
                    <td className="text-muted small">{i + 1}</td>
                    <td>
                      <div className="small fw-semibold">{q.question?.substring(0, 120)}{q.question?.length > 120 ? '…' : ''}</div>
                      {q.options?.length > 0 && (
                        <div className="mt-1">
                          {q.options.map((o, j) => (
                            <span key={j} className={`badge me-1 ${o.isCorrect ? 'bg-success' : 'bg-light text-dark border'}`} style={{ fontSize: 11 }}>
                              {['A','B','C','D','E'][j]}) {o.text?.substring(0, 30)}{o.text?.length > 30 ? '…' : ''}
                            </span>
                          ))}
                        </div>
                      )}
                    </td>
                    <td><span className="badge bg-secondary-subtle text-dark border small">{Q_TYPES.find(t => t.value === q.type)?.label || q.type}</span></td>
                    <td><span className={`badge small bg-${q.difficulty === 'easy' ? 'success' : q.difficulty === 'hard' ? 'danger' : 'warning'}-subtle text-dark border`}>{q.difficulty || 'medium'}</span></td>
                    <td>{q.marks || 1}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </BSModal>
  );
};

// ─── Student Result View ───────────────────────────────────────────────────
// Shows a student their submission with grade, percentage, and per-question
// feedback. Re-uses data fetched via /my so the student always sees the
// latest grade the teacher entered.
const StudentResultView = ({ result }) => {
  if (!result) return null;
  const { assessment, submission } = result;
  const totalMarks = assessment?.totalMarks || 0;
  const passingMarks = assessment?.passingMarks || 0;
  const marks = submission.marksObtained || 0;
  const pct = submission.percentage || 0;
  const passed = submission.passed;
  const evaluated = submission.status === 'evaluated';

  return (
    <div>
      {/* Score banner */}
      <div className={`alert ${passed === true ? 'alert-success' : passed === false ? 'alert-danger' : evaluated ? 'alert-info' : 'alert-warning'}`}>
        <div className="d-flex justify-content-between align-items-center">
          <div>
            <div className="fw-bold" style={{ fontSize: 28 }}>{marks} <small className="fs-6 fw-normal text-muted">/ {totalMarks}</small></div>
            <div className="small text-muted">marks obtained</div>
          </div>
          <div className="text-center">
            <div className="fw-bold" style={{ fontSize: 36, lineHeight: 1 }}>{pct}%</div>
            <small className="text-muted">percentage</small>
          </div>
          <div className="text-end">
            {evaluated ? (
              <span className={`badge bg-${passed === true ? 'success' : passed === false ? 'danger' : 'secondary'} fs-6`}>
                <i className={`bi ${passed === true ? 'bi-check-circle-fill' : passed === false ? 'bi-x-circle-fill' : 'bi-dash-circle'} me-1`} />
                {passed === true ? 'Passed' : passed === false ? 'Failed' : 'Reviewed'}
              </span>
            ) : (
              <span className="badge bg-warning text-dark fs-6">
                <i className="bi bi-hourglass-split me-1" />Pending review
              </span>
            )}
            <div className="small text-muted mt-1">passing: {passingMarks}/{totalMarks}</div>
          </div>
        </div>
      </div>

      {/* Overall teacher feedback */}
      {submission.feedback && (
        <div className="mb-3">
          <label className="form-label small text-muted mb-1">Teacher's feedback</label>
          <div className="border rounded p-2 bg-light small" style={{ whiteSpace: 'pre-wrap' }}>
            {submission.feedback}
          </div>
        </div>
      )}

      {/* Per-question review */}
      {(assessment?.questions || []).length > 0 && (
        <div>
          <label className="form-label small text-muted mb-2">Question-by-question review</label>
          <div style={{ maxHeight: 320, overflowY: 'auto' }}>
            {(assessment.questions || []).map((q, i) => {
              const ans = (submission.answers || []).find(a => String(a.questionId) === String(q._id));
              const opts = (q.options || []).map(o => o.text);
              const correct = (q.options || []).filter(o => o.isCorrect).map(o => o.text);
              const sa = ans?.answer;
              return (
                <div key={q._id} className="border rounded p-2 mb-2 small">
                  <div className="d-flex justify-content-between">
                    <strong>Q{i+1}. {q.text || q.question || '(question text)'}</strong>
                    <span className={`badge ${ans?.marksAwarded != null ? (ans.marksAwarded >= (q.marks || 0) * 0.5 ? 'bg-success' : 'bg-warning text-dark') : 'bg-light text-muted'}`}>
                      {ans?.marksAwarded != null ? `${ans.marksAwarded}/${q.marks || 0}` : `–/${q.marks || 0}`}
                    </span>
                  </div>
                  {sa != null && sa !== '' && (
                    <div className="mt-1"><span className="text-muted">Your answer: </span><span>{Array.isArray(sa) ? sa.join(', ') : String(sa)}</span></div>
                  )}
                  {correct.length > 0 && (
                    <div className="mt-1"><span className="text-muted">Correct: </span><span className="text-success">{correct.join(', ')}</span></div>
                  )}
                  {ans?.feedback && (
                    <div className="mt-1"><span className="text-muted">Feedback: </span><span className="text-info">{ans.feedback}</span></div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div className="small text-muted text-end">
        {submission.submittedAt && <>Submitted: {new Date(submission.submittedAt).toLocaleString()}</>}
        {submission.evaluatedAt && <> · Evaluated: {new Date(submission.evaluatedAt).toLocaleString()}</>}
      </div>
    </div>
  );
};

// ─── Submissions Dialog ──────────────────────────────────────────────────────

const SubmissionsDialog = ({ show, assessment, onClose, onAlert, canWrite }) => {
  const [data, setData]   = useState(null);
  const [grading, setGrading] = useState(null); // { submissionId, answers, remarks }
  const [saving, setSaving]   = useState(false);

  useEffect(() => {
    if (!show || !assessment) return;
    api.get(`/training/assessments/${assessment._id}`)
      .then(r => setData(r.data?.data || r.data))
      .catch(() => {});
    return () => setData(null);
  }, [show, assessment]);

  const openGrade = (sub) => {
    setGrading({
      submissionId: sub._id,
      remarks: sub.feedback || '',
      submissionAnswers: sub.answers || [],
      answers: (sub.answers||[]).map(a => ({ questionId: a.questionId, marksAwarded: a.marksAwarded || 0, feedback: a.feedback || '' })),
    });
  };

  const fmtStudentAnswer = (ans) => {
    if (ans == null || ans === '') return <span className="text-muted fst-italic">Not answered</span>;
    if (Array.isArray(ans)) return ans.length ? ans.join(', ') : <span className="text-muted fst-italic">Not answered</span>;
    return String(ans);
  };

  const saveGrade = async () => {
    setSaving(true);
    try {
      await api.put(`/training/assessments/${assessment._id}/grade/${grading.submissionId}`, {
        answers: grading.answers, remarks: grading.remarks,
      });
      // Refresh
      const r = await api.get(`/training/assessments/${assessment._id}`);
      setData(r.data?.data || r.data);
      setGrading(null);
      onAlert('Graded successfully.', 'success');
    } catch (e) { onAlert(e.response?.data?.message || 'Failed to grade.', 'danger'); }
    setSaving(false);
  };

  const subs = data?.submissions || [];

  return (
    <BSModal show={show} onClose={onClose} title={`Submissions — ${assessment?.title}`} size="modal-xl">
      {!data ? (
        <div className="text-center py-4"><div className="spinner-border text-primary" /></div>
      ) : subs.length === 0 ? (
        <div className="alert alert-info">No submissions yet.</div>
      ) : (
        <div className="table-responsive">
          <table className="table table-sm table-bordered align-middle">
            <thead className="table-light">
              <tr>
                <th>Student</th>
                <th>Submitted</th>
                <th>Score</th>
                <th>%</th>
                <th>Status</th>
                <th>Violations</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {subs.map(sub => (
                <tr key={sub._id}>
                  <td><div className="fw-semibold">{sub.student?.firstName} {sub.student?.lastName}</div><small className="text-muted">{sub.student?.email}</small></td>
                  <td><small>{fmtDateTime(sub.submittedAt)}</small></td>
                  <td>{sub.marksObtained} / {data.totalMarks}</td>
                  <td>
                    <span className={`badge bg-${sub.percentage >= 75 ? 'success' : sub.percentage >= 50 ? 'warning' : 'danger'}`}>
                      {sub.percentage}%
                    </span>
                  </td>
                  <td><span className={`badge bg-${sub.status==='graded'?'success':sub.status==='pending_review'?'warning':'info'}`}>{sub.status}</span></td>
                  <td>{sub.violations?.length > 0 ? <span className="badge bg-danger">{sub.violations.length}</span> : '—'}</td>
                  <td>
                    {canWrite ? (
                      <button className="btn btn-outline-primary btn-sm" onClick={() => openGrade(sub)}>
                        <i className="bi bi-pencil-square me-1" />Grade / Review
                      </button>
                    ) : (
                      <button className="btn btn-outline-secondary btn-sm" onClick={() => openGrade(sub)}>
                        <i className="bi bi-eye me-1" />View Answers
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Grade panel */}
      {grading && data && (
        <div className="mt-3 border rounded p-3">
          <h6 className="fw-bold mb-3">Review & Grade Answers</h6>
          {grading.answers.map((a, i) => {
            const q = data.questions.find(x => String(x._id) === a.questionId);
            const subAns = (grading.submissionAnswers || []).find(x => String(x.questionId) === a.questionId);
            const studentAnswer = subAns?.answer;
            const correctTexts = (q?.options || []).filter(o => o.isCorrect).map(o => o.text);
            const correctDisplay = correctTexts.length
              ? correctTexts.join(', ')
              : (q?.expectedAnswer || q?.correctAnswers?.join(', ') || '—');
            const hasOptions = (q?.options || []).length > 0;
            const OPT_LETTERS = ['A','B','C','D','E','F'];
            return (
              <div key={i} className="mb-3 p-2 border rounded">
                <div className="small fw-semibold mb-2">
                  <span className="text-muted me-1">Q{i+1}.</span>
                  {q?.question || `Question ${i+1}`}
                  <span className="text-muted ms-2">(max {q?.marks||0} mark{q?.marks !== 1 ? 's' : ''})</span>
                </div>

                {/* Student's answer */}
                <div className="mb-2">
                  <div className="small text-muted mb-1">Student's answer:</div>
                  {hasOptions ? (
                    <div className="ms-1">
                      {q.options.map((opt, oi) => {
                        const picked = Array.isArray(studentAnswer) ? studentAnswer.includes(opt.text) : studentAnswer === opt.text;
                        return (
                          <div key={oi} className={`d-flex align-items-center gap-2 rounded border px-2 py-1 mb-1 small
                            ${picked && opt.isCorrect  ? 'bg-success-subtle border-success'
                            : picked && !opt.isCorrect ? 'bg-danger-subtle border-danger'
                            : !picked && opt.isCorrect ? 'bg-success-subtle border-success opacity-75'
                            : 'bg-light border-light text-muted'}`}>
                            <span className="fw-semibold" style={{ width: 18 }}>{OPT_LETTERS[oi]})</span>
                            <span className="flex-fill">{opt.text}</span>
                            {picked && opt.isCorrect  && <i className="bi bi-check-circle-fill text-success" title="Student selected — correct" />}
                            {picked && !opt.isCorrect && <i className="bi bi-x-circle-fill text-danger"   title="Student selected — wrong" />}
                            {!picked && opt.isCorrect && <i className="bi bi-check-circle text-success"   title="Correct answer" />}
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="ms-1">
                      <div className="p-2 bg-light rounded border small">{fmtStudentAnswer(studentAnswer)}</div>
                      {q?.expectedAnswer && (
                        <div className="mt-1 small text-success-emphasis">
                          <i className="bi bi-check-circle me-1" /><strong>Reference answer:</strong> {q.expectedAnswer}
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* Auto-grade indicator */}
                {hasOptions && (
                  <div className="small text-muted mb-2">
                    <strong>Correct answer:</strong> {correctDisplay}
                    {' · '}<strong>Auto marks:</strong> {a.marksAwarded}
                  </div>
                )}

                {/* Marks + feedback override */}
                <div className="row g-2 mt-1">
                  <div className="col-4">
                    <label className="form-label small mb-0">Override Marks</label>
                    <input className="form-control form-control-sm" type="number" min="0" max={q?.marks||100} value={a.marksAwarded}
                      onChange={e => { const upd=[...grading.answers]; upd[i]={...a,marksAwarded:Number(e.target.value)}; setGrading(p=>({...p,answers:upd})); }} />
                  </div>
                  <div className="col-8">
                    <label className="form-label small mb-0">Feedback to Student</label>
                    <input className="form-control form-control-sm" value={a.feedback}
                      onChange={e => { const upd=[...grading.answers]; upd[i]={...a,feedback:e.target.value}; setGrading(p=>({...p,answers:upd})); }} />
                  </div>
                </div>
              </div>
            );
          })}
          <div className="mb-2">
            <label className="form-label small">Overall Remarks</label>
            <textarea className="form-control form-control-sm" rows={2} value={grading.remarks} onChange={e => setGrading(p=>({...p,remarks:e.target.value}))} />
          </div>
          <div className="d-flex gap-2 justify-content-end">
            <button className="btn btn-secondary btn-sm" onClick={() => setGrading(null)}>Cancel</button>
            <button className="btn btn-success btn-sm" onClick={saveGrade} disabled={saving}>
              {saving ? <span className="spinner-border spinner-border-sm" /> : 'Save Grade'}
            </button>
          </div>
        </div>
      )}
    </BSModal>
  );
};

// ─── TakeAssessmentModal ─────────────────────────────────────────────────────

const TakeAssessmentModal = ({ show, assessment, onClose, onSubmit, onAlert }) => {
  const [answers, setAnswers] = useState({});
  const [timeLeft, setTimeLeft] = useState(null);
  const [violations, setViolations] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [page, setPage] = useState(0);
  const [stage, setStage] = useState('instructions');   // 'instructions' | 'attempt' | 'review'
  const [acknowledged, setAcknowledged] = useState(false);
  const [submitResult, setSubmitResult] = useState(null); // { marksObtained, percentage, passed }
  const [codeLang, setCodeLang] = useState('javascript');
  const [codeOutput, setCodeOutput] = useState(null); // { stdout, stderr, exitCode, runtime }
  const [runningCode, setRunningCode] = useState(false);
  const [codeExecResults, setCodeExecResults] = useState({}); // { [qId]: { stdout, stderr, exitCode, language } }
  const [editorHeight, setEditorHeight] = useState(280); // resizable editor height

  const questions = assessment?.questions || [];
  const isStrict = assessment?.attemptMode === 'strict';
  const isRestricted = assessment?.attemptMode === 'restricted' || isStrict;
  const total = questions.length;
  const cur = questions[page] || null;

  // Timer — only starts once the student has acknowledged instructions
  useEffect(() => {
    if (!show || stage !== 'attempt' || !assessment?.duration) return;
    setTimeLeft(assessment.duration * 60);
    const id = setInterval(() => setTimeLeft(t => {
      if (t <= 1) { clearInterval(id); handleSubmit(true); return 0; }
      return t - 1;
    }), 1000);
    return () => clearInterval(id);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, stage, assessment?._id]);

  // Anti-cheat: track tab switches (only during the attempt)
  useEffect(() => {
    if (!show || stage !== 'attempt' || !isRestricted) return;
    const onBlur = () => setViolations(v => [...v, { type: 'tab_switch', details: new Date().toISOString() }]);
    window.addEventListener('blur', onBlur);
    return () => window.removeEventListener('blur', onBlur);
  }, [show, stage, isRestricted]);

  // Reset state when modal opens or assessment changes
  useEffect(() => {
    if (show) {
      setAnswers({}); setPage(0); setViolations([]); setSubmitting(false);
      setStage('instructions'); setAcknowledged(false); setSubmitResult(null);
    }
  }, [show, assessment?._id]);

  const setAnswer = (qId, value) => setAnswers(prev => ({ ...prev, [qId]: value }));

  const handleSubmit = async (autoSubmit = false) => {
    if (submitting) return;
    if (!autoSubmit && !window.confirm('Submit assessment? This cannot be undone.')) return;
    setSubmitting(true);
    const answerArr = questions.map(q => {
      const exec = codeExecResults[q._id];
      return {
        questionId: q._id,
        answer: answers[q._id] ?? (q.type === 'mcq_multi' ? [] : ''),
        ...(exec ? { codeOutput: exec.stdout, codeError: exec.stderr, codeExitCode: exec.exitCode, codeLanguage: exec.language, codeRanAt: new Date().toISOString() } : {}),
      };
    });
    try {
      const result = await onSubmit(answerArr, violations);
      if (assessment.allowReview) {
        setSubmitResult(result || {});
        setStage('review');
      } else {
        onClose();
      }
    } catch (e) {
      onAlert(e.response?.data?.message || 'Submit failed.', 'danger');
    }
    setSubmitting(false);
  };

  const runCode = async () => {
    const code = answers[cur?._id] || '';
    if (!code.trim()) { onAlert('Write some code first.', 'warning'); return; }
    setRunningCode(true);
    try {
      const r = await api.post('/training/assessments/execute', { code, language: codeLang });
      const result = r.data?.data || r.data || {};
      const execResult = { stdout: result.stdout || '', stderr: result.stderr || '', exitCode: result.exitCode ?? 0, language: codeLang };
      setCodeOutput(execResult);
      setCodeExecResults(prev => ({ ...prev, [cur._id]: execResult }));
    } catch (e) {
      const errResult = { stdout: '', stderr: e.response?.data?.message || e.message || 'Execution failed', exitCode: 1, language: codeLang };
      setCodeOutput(errResult);
      setCodeExecResults(prev => ({ ...prev, [cur._id]: errResult }));
    }
    setRunningCode(false);
  };

  const fmtTime = (s) => `${String(Math.floor(s/60)).padStart(2,'0')}:${String(s%60).padStart(2,'0')}`;

  if (!show || !assessment) return null;

  // ── Instructions gate ────────────────────────────────────────────────
  if (stage === 'instructions') {
    const totalMarks = questions.reduce((s, q) => s + (q.marks || 0), 0);
    return (
      <div className="modal fade show d-block" style={{ background: 'rgba(0,0,0,0.8)', zIndex: 1060 }}>
        <div className="modal-dialog modal-lg modal-dialog-scrollable modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-header bg-primary text-white">
              <h5 className="modal-title mb-0">{assessment.title}</h5>
              <button className="btn-close btn-close-white" onClick={onClose} />
            </div>
            <div className="modal-body">
              {assessment.description && <p className="text-muted">{assessment.description}</p>}

              <div className="row g-2 mb-3">
                <div className="col-6 col-md-3">
                  <div className="border rounded p-2 text-center bg-light">
                    <div className="small text-muted">Questions</div>
                    <div className="fw-bold fs-5">{total}</div>
                  </div>
                </div>
                <div className="col-6 col-md-3">
                  <div className="border rounded p-2 text-center bg-light">
                    <div className="small text-muted">Total Marks</div>
                    <div className="fw-bold fs-5">{totalMarks}</div>
                  </div>
                </div>
                <div className="col-6 col-md-3">
                  <div className="border rounded p-2 text-center bg-light">
                    <div className="small text-muted">Duration</div>
                    <div className="fw-bold fs-5">{assessment.duration ? `${assessment.duration} min` : 'Unlimited'}</div>
                  </div>
                </div>
                <div className="col-6 col-md-3">
                  <div className="border rounded p-2 text-center bg-light">
                    <div className="small text-muted">Pass Marks</div>
                    <div className="fw-bold fs-5">{assessment.passingMarks || '—'}</div>
                  </div>
                </div>
              </div>

              <div className="alert alert-info">
                <h6 className="alert-heading"><i className="bi bi-info-circle me-2" />Instructions</h6>
                {assessment.instructions ? (
                  <div style={{ whiteSpace: 'pre-wrap' }}>{assessment.instructions}</div>
                ) : (
                  <div className="text-muted small">No specific instructions provided — read each question carefully and answer honestly.</div>
                )}
              </div>

              {isRestricted && (
                <div className="alert alert-warning small mb-3">
                  <strong><i className="bi bi-shield-exclamation me-1" />Proctored assessment</strong>
                  <ul className="mb-0 ps-3 mt-1">
                    <li>Do not switch tabs or windows — tab-switching will be logged as a violation.</li>
                    {isStrict && <li>Exceeding {assessment.violationLimit || 3} violations will automatically invalidate your attempt.</li>}
                    <li>Keep this tab focused throughout the assessment.</li>
                  </ul>
                </div>
              )}

              {assessment.attemptLimit > 1 && (
                <div className="small text-muted mb-3">
                  <i className="bi bi-arrow-repeat me-1" />
                  You can attempt this assessment up to <strong>{assessment.attemptLimit}</strong> times.
                </div>
              )}

              <div className="form-check mt-4">
                <input className="form-check-input" type="checkbox" id="ack-instructions"
                       checked={acknowledged} onChange={e => setAcknowledged(e.target.checked)} />
                <label className="form-check-label" htmlFor="ack-instructions">
                  I have read and understood the instructions, and I'm ready to begin.
                </label>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
              <button className="btn btn-primary" disabled={!acknowledged} onClick={() => setStage('attempt')}>
                <i className="bi bi-play-fill me-1" />
                {assessment.duration ? `Start (${assessment.duration} min timer)` : 'Start Assessment'}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ── Review stage ─────────────────────────────────────────────────────
  if (stage === 'review' && submitResult !== null) {
    const { marksObtained = 0, percentage = 0, passed } = submitResult;
    const OPT_LETTERS = ['A','B','C','D','E','F'];

    const computeCorrect = (q) => {
      const sa = answers[q._id];
      const correctTexts = (q.options || []).filter(o => o.isCorrect).map(o => o.text);
      if (q.type === 'mcq_single' || q.type === 'true_false') {
        const chosen = Array.isArray(sa) ? sa[0] : sa;
        return chosen != null && chosen !== '' && correctTexts.some(c => String(c) === String(chosen));
      }
      if (q.type === 'mcq_multi') {
        const sa2 = (Array.isArray(sa) ? sa : []).map(String).sort();
        const ca  = correctTexts.map(String).sort();
        return sa2.length > 0 && sa2.length === ca.length && sa2.every((v, i) => v === ca[i]);
      }
      if (q.type === 'short_answer' && q.expectedAnswer) {
        return (sa || '').trim().toLowerCase() === q.expectedAnswer.trim().toLowerCase();
      }
      return null; // needs manual grading
    };

    return (
      <div className="modal fade show d-block" style={{ background: 'rgba(0,0,0,0.7)', zIndex: 1060 }}>
        <div className="modal-dialog modal-lg modal-dialog-scrollable modal-dialog-centered">
          <div className="modal-content">
            <div className={`modal-header text-white ${passed === true ? 'bg-success' : passed === false ? 'bg-danger' : 'bg-primary'}`}>
              <div>
                <h5 className="modal-title mb-0">{assessment.title} — Results</h5>
                <small>{passed === true ? 'Passed' : passed === false ? 'Failed' : 'Submitted — pending review'}</small>
              </div>
            </div>
            <div className="modal-body">
              {/* Score banner */}
              <div className={`alert mb-3 ${passed === true ? 'alert-success' : passed === false ? 'alert-danger' : 'alert-info'}`}>
                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    <div className="fw-bold fs-4">{marksObtained} <small className="fs-6 fw-normal text-muted">/ {assessment.totalMarks}</small></div>
                    <div className="small text-muted">marks obtained</div>
                  </div>
                  <div className="text-center">
                    <div className="fw-bold" style={{ fontSize: 40, lineHeight: 1 }}>{percentage}%</div>
                    {passed !== undefined && (
                      <span className={`badge mt-1 ${passed ? 'bg-success' : 'bg-danger'}`}>
                        <i className={`bi ${passed ? 'bi-check-circle' : 'bi-x-circle'} me-1`} />
                        {passed ? 'Passed' : 'Failed'}
                      </span>
                    )}
                  </div>
                  <div className="text-end">
                    <div className="fw-bold">{assessment.passingMarks || '—'}</div>
                    <div className="small text-muted">passing marks</div>
                  </div>
                </div>
              </div>

              {/* Per-question review */}
              {questions.map((q, qi) => {
                const sa         = answers[q._id];
                const isCorrect  = computeCorrect(q);
                const hasOptions = (q.options || []).length > 0;
                const answered   = sa !== undefined && sa !== '' && !(Array.isArray(sa) && sa.length === 0);

                return (
                  <div key={q._id} className={`card mb-2 border-${isCorrect === true ? 'success' : isCorrect === false ? 'danger' : 'secondary'}`}>
                    <div className="card-body py-2 px-3">
                      <div className="d-flex justify-content-between align-items-start gap-2 mb-2">
                        <div className="small fw-semibold" style={{ flex: 1 }}>
                          <span className="badge bg-light text-dark border me-2">{qi + 1}</span>
                          {q.question}
                        </div>
                        <div className="text-nowrap">
                          {!answered
                            ? <span className="badge bg-secondary">Not answered</span>
                            : isCorrect === true
                            ? <span className="badge bg-success"><i className="bi bi-check-lg me-1" />{q.marks} mark{q.marks !== 1 ? 's' : ''}</span>
                            : isCorrect === false
                            ? <span className="badge bg-danger"><i className="bi bi-x-lg me-1" />0 marks</span>
                            : <span className="badge bg-warning text-dark">Pending review</span>}
                        </div>
                      </div>

                      {/* MCQ / True-False options with colour coding */}
                      {hasOptions && (
                        <div className="ms-2">
                          {q.options.map((opt, oi) => {
                            const picked = Array.isArray(sa) ? sa.includes(opt.text) : sa === opt.text;
                            const cls = picked && opt.isCorrect   ? 'bg-success-subtle border-success'
                                      : picked && !opt.isCorrect  ? 'bg-danger-subtle border-danger'
                                      : !picked && opt.isCorrect  ? 'bg-success-subtle border-success'
                                      : 'bg-light border-light';
                            return (
                              <div key={oi} className={`d-flex align-items-center gap-2 rounded border px-2 py-1 mb-1 small ${cls}`}>
                                <span className="fw-semibold" style={{ width: 18 }}>{OPT_LETTERS[oi]})</span>
                                <span className="flex-fill">{opt.text}</span>
                                {picked && opt.isCorrect  && <i className="bi bi-check-circle-fill text-success" title="Your answer — correct" />}
                                {picked && !opt.isCorrect && <i className="bi bi-x-circle-fill text-danger"   title="Your answer — wrong" />}
                                {!picked && opt.isCorrect && <i className="bi bi-check-circle text-success"   title="Correct answer" />}
                              </div>
                            );
                          })}
                        </div>
                      )}

                      {/* Open-ended answer */}
                      {!hasOptions && (
                        <div className="ms-2 small">
                          <div className="mb-1">
                            <strong>Your answer: </strong>
                            <span className={answered ? '' : 'text-muted fst-italic'}>{answered ? String(sa) : 'Not answered'}</span>
                          </div>
                          {q.expectedAnswer && (
                            <div className="text-success-emphasis"><strong>Reference answer: </strong>{q.expectedAnswer}</div>
                          )}
                        </div>
                      )}

                      {/* Explanation */}
                      {q.explanation && (
                        <div className="ms-2 mt-2 pt-1 border-top small text-muted fst-italic">
                          <i className="bi bi-lightbulb-fill text-warning me-1" />{q.explanation}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="modal-footer">
              <button className="btn btn-primary" onClick={onClose}><i className="bi bi-check2 me-1" />Done</button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ── Attempt stage ────────────────────────────────────────────────────
  const selectedFor = (qId) => answers[qId];
  const hasAnswer = (qId, type) => {
    const v = answers[qId];
    if (type === 'mcq_multi') return Array.isArray(v) && v.length > 0;
    return v !== undefined && v !== '' && v !== null;
  };

  return (
    <div className="modal fade show d-block" style={{ background:'rgba(0,0,0,0.8)', zIndex:1060 }}>
      <div className={`modal-dialog ${cur?.type === 'coding' ? 'modal-fullscreen' : 'modal-xl'} modal-dialog-scrollable modal-dialog-centered`}>
        <div className="modal-content" style={{ maxHeight: '90vh' }}>
          <div className="modal-header bg-primary text-white">
            <div>
              <h5 className="modal-title mb-0">{assessment.title}</h5>
              <small>{page+1}/{total} questions</small>
            </div>
            <div className="d-flex align-items-center gap-3">
              {timeLeft !== null && (
                <span className={`badge fs-6 ${timeLeft < 300 ? 'bg-danger' : 'bg-light text-dark'}`}>
                  <i className="bi bi-clock me-1"/>{fmtTime(timeLeft)}
                </span>
              )}
              {isRestricted && violations.length > 0 && (
                <span className="badge bg-warning text-dark">{violations.length} violation{violations.length>1?'s':''}</span>
              )}
            </div>
          </div>
          <div className="modal-body p-0" style={{ overflow: 'auto', maxHeight: 'calc(90vh - 160px)' }}>
            {/* Progress bar */}
            <div className="px-3 pt-2 pb-1">
              <div className="progress" style={{height:6}}>
                <div className="progress-bar" style={{width:`${((page+1)/total)*100}%`}}/>
              </div>
            </div>

<div className="question-content">{cur && cur.type !== 'coding' && (
              <div className="px-3 pb-3">
                <div className="card border-0 shadow-sm">
                  <div className="card-body">
                    <div className="d-flex gap-2 mb-3">
                      <span className="badge bg-secondary">{page+1}</span>
                      <span className="badge bg-light text-dark border">
                        {Q_TYPES.find(t => t.value === cur.type)?.label || cur.type}
                      </span>
                      <span className="badge bg-light text-dark border">{cur.marks} mark{cur.marks!==1?'s':''}</span>
                    </div>
                    <p className="fs-6 fw-semibold" style={{ whiteSpace: 'pre-wrap' }}>
                      {cur.question || cur.questionText}
                    </p>

                    {cur.type === 'mcq_single' && (
                      <div className="list-group">
                        {(cur.options||[]).map((opt, i) => {
                          const text = typeof opt === 'string' ? opt : opt.text;
                          const checked = selectedFor(cur._id) === text;
                          return (
                            <label key={i} className={`list-group-item list-group-item-action d-flex gap-2 align-items-center ${checked ? 'active' : ''}`}
                                   style={{ cursor: 'pointer' }}>
                              <input type="radio" className="form-check-input m-0 me-1" name={`q-${cur._id}`}
                                     checked={checked} onChange={() => setAnswer(cur._id, text)} />
                              <span className="badge bg-secondary rounded-circle" style={{ width: 22, height: 22, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11 }}>
                                {['A','B','C','D','E','F'][i] || i+1}
                              </span>
                              <span>{text}</span>
                            </label>
                          );
                        })}
                      </div>
                    )}

                    {cur.type === 'mcq_multi' && (
                      <div className="list-group">
                        {(cur.options||[]).map((opt, i) => {
                          const text = typeof opt === 'string' ? opt : opt.text;
                          const checked = (answers[cur._id] || []).includes(text);
                          return (
                            <label key={i} className={`list-group-item list-group-item-action d-flex gap-2 align-items-center ${checked ? 'active' : ''}`}
                                   style={{ cursor: 'pointer' }}>
                              <input type="checkbox" className="form-check-input m-0 me-1"
                                     checked={checked} onChange={() => {
                                       const cur = answers[cur._id] || [];
                                       const next = checked ? cur.filter(t => t !== text) : [...cur, text];
                                       setAnswer(cur._id, next);
                              }}/>
                              <span className="badge bg-secondary rounded-circle" style={{ width: 22, height: 22, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11 }}>
                                {['A','B','C','D','E','F'][i] || i+1}
                              </span>
                              <span>{text}</span>
                            </label>
                          );
                        })}
                      </div>
                    )}

                    {cur.type === 'true_false' && (
                      <div className="list-group">
                        {[true, false].map((val, i) => {
                          const checked = selectedFor(cur._id) === val;
                          return (
                            <label key={i} className={`list-group-item list-group-item-action d-flex gap-2 align-items-center ${checked ? 'active' : ''}`}
                                   style={{ cursor: 'pointer' }}>
                              <input type="radio" className="form-check-input m-0 me-1" name={`q-${cur._id}`}
                                     checked={checked} onChange={() => setAnswer(cur._id, val)} />
                              <span>{val ? 'True' : 'False'}</span>
                            </label>
                          );
                        })}
                      </div>
                    )}

                    {cur.type === 'short_answer' && (
                      <input type="text" className="form-control mt-2"
                             placeholder="Your answer…" value={answers[cur._id] || ''}
                             onChange={e => setAnswer(cur._id, e.target.value)} />
                    )}

                    {cur.type === 'practical' ? (
                      <textarea
                        className="form-control mt-2"
                        rows={40}
                        style={{ minHeight: 600 }}
                        placeholder="Describe your approach, methodology, and results…"
                        value={answers[cur._id] || ''}
                        onChange={e => setAnswer(cur._id, e.target.value)}
                      />
                    ) : cur.type === 'long_answer' ? (
                      <textarea
                        className="form-control mt-2"
                        rows={40}
                        style={{ minHeight: 600 }}
                        placeholder="Your answer…"
                        value={answers[cur._id] || ''}
                        onChange={e => setAnswer(cur._id, e.target.value)}
                      />
                    ) : null}

                    {cur.type === 'mcq' && (
                      <div className="list-group">
                        {(cur.options||[]).map((opt, i) => {
                          const text = typeof opt === 'string' ? opt : opt.text;
                          const checked = selectedFor(cur._id) === text;
                          return (
                            <label key={i} className={`list-group-item ${checked ? 'active' : ''}`} style={{ cursor:'pointer' }}>
                              <input type="radio" className="form-check-input me-2" name={`q-${cur._id}`}
                                     checked={checked} onChange={() => setAnswer(cur._id, text)} />
                              {text}
                            </label>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {cur && cur.type === 'coding' && (
              <div className="px-3">
                {/* Question header */}
                <div className="px-3 py-2 border-bottom bg-light flex-shrink-0">
                  <div className="d-flex gap-2 mb-2">
                    <span className="badge bg-secondary">{page+1}</span>
                    <span className="badge bg-primary border">Coding</span>
                    <span className="badge bg-light text-dark border">{cur.marks} mark{cur.marks!==1?'s':''}</span>
                  </div>
                  <p className="mb-0 fw-semibold" style={{ whiteSpace: 'pre-wrap' }}>{cur.question || cur.questionText}</p>
                  {cur.codeTemplate && (
                    <div className="small text-muted mt-1">
                      <i className="bi bi-code-square me-1"/>Starter code provided below
                    </div>
                  )}
                </div>

                {/* Toolbar: language selector + run button */}
                <div className="d-flex align-items-center gap-2 px-3 py-2 border-bottom bg-white flex-shrink-0">
                  <label className="small fw-semibold mb-0">Language:</label>
                  <select className="form-select form-select-sm" style={{ width: 140 }}
                    value={codeLang} onChange={e => setCodeLang(e.target.value)}>
                    {CODE_LANGUAGES.map(l => <option key={l.value} value={l.value}>{l.label}</option>)}
                  </select>
                  <button className="btn btn-success btn-sm" onClick={runCode} disabled={runningCode}>
                    {runningCode ? <><span className="spinner-border spinner-border-sm me-1"/>Running…</> : <><i className="bi bi-play-fill me-1"/>Run Code</>}
                  </button>
                  {codeOutput && (
                    <span className={`badge ${codeOutput.exitCode === 0 ? 'bg-success' : 'bg-danger'} ms-auto`}>
                      Exit code: {codeOutput.exitCode}
                    </span>
                  )}
                </div>

                {/* Code editor — resizable */}
                <div style={{ height: editorHeight, maxHeight: 600, position: 'relative' }} className="border">
                  <Editor
                    height="100%"
                    language={CODE_LANGUAGES.find(l => l.value === codeLang)?.monaco || 'javascript'}
                    value={answers[cur._id] || cur.codeTemplate || DEFAULT_TEMPLATES[codeLang] || ''}
                    onChange={v => setAnswer(cur._id, v || '')}
                    theme="vs-dark"
                    options={{ fontSize: 14, minimap: { enabled: false }, scrollBeyondLastLine: false, lineNumbers: 'on', wordWrap: 'on', automaticLayout: true, tabSize: 2, padding: { top: 8 } }}
                  />
                  {/* Resize handle — thin bar at bottom */}
                  <div
                    style={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: 8, cursor: 'ns-resize', zIndex: 10, background: '#dee2e6', opacity: 0.5 }}
                    onMouseDown={e => {
                      e.preventDefault();
                      const startY = e.clientY;
                      const startH = editorHeight;
                      const onMove = (ev) => {
                        const delta = startY - ev.clientY;
                        const newH = Math.min(600, Math.max(180, startH + delta));
                        setEditorHeight(newH);
                      };
                      const onUp = () => { document.removeEventListener('mousemove', onMove); document.removeEventListener('mouseup', onUp); };
                      document.addEventListener('mousemove', onMove);
                      document.addEventListener('mouseup', onUp);
                    }}
                    title="Drag to resize editor"
                  />
                </div>

                {/* Output panel — always shown when code has been run */}
                {codeOutput !== null && codeOutput !== undefined && (
                  <div style={{ background: '#1e1e1e' }} className="border border-top-0">
                    <div className="d-flex align-items-center gap-2 px-3 py-1 bg-secondary">
                      <i className="bi bi-terminal me-1 text-white"/><span className="text-white small fw-semibold">Output</span>
                      <span className={`badge ms-2 ${codeOutput.exitCode === 0 ? 'bg-success' : 'bg-danger'} ms-auto`} style={{ fontSize: 10 }}>
                        Exit: {codeOutput.exitCode ?? '?'}
                      </span>
                      <button className="btn btn-sm btn-outline-light ms-2 py-0" style={{ lineHeight: 1 }} onClick={() => setCodeOutput(null)} title="Clear output">
                        <i className="bi bi-x"></i>
                      </button>
                    </div>
                    <div style={{ maxHeight: 150, overflow: 'auto' }}>
                      {codeOutput.stdout && (
                        <pre className="mb-0 px-3 py-2 small text-white" style={{ whiteSpace: 'pre-wrap', fontSize: 12 }}>{codeOutput.stdout}</pre>
                      )}
                      {codeOutput.stderr && (
                        <pre className="mb-0 px-3 py-2 small text-danger" style={{ whiteSpace: 'pre-wrap', fontSize: 12 }}>{codeOutput.stderr}</pre>
                      )}
                      {!codeOutput.stdout && !codeOutput.stderr && (
                        <div className="px-3 py-2 text-muted small">No output produced</div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}
            </div>
            <div className="d-flex gap-2">
              <button className="btn btn-outline-secondary btn-sm" disabled={page===0} onClick={()=>setPage(p=>p-1)}>
                <i className="bi bi-chevron-left"/> Prev
              </button>
              <button className="btn btn-outline-secondary btn-sm" disabled={page===total-1} onClick={()=>setPage(p=>p+1)}>
                Next <i className="bi bi-chevron-right"/>
              </button>
            </div>
            <div className="d-flex gap-2 align-items-center">
              <div className="d-flex gap-1 align-items-center flex-wrap" style={{ maxWidth: 300, overflowX: 'auto' }}>
                {questions.map((q, i) => (
                  <button key={i}
                          className={`btn btn-sm p-0 rounded-circle flex-shrink-0 ${hasAnswer(q._id, q.type) ? 'btn-success' : 'btn-outline-secondary'}`}
                          style={{ width:18, height:18, fontSize:9 }} onClick={() => setPage(i)}
                          title={hasAnswer(q._id, q.type) ? 'Answered' : 'Unanswered'}>
                    {i+1}
                  </button>
                ))}
              </div>
              <button className="btn btn-primary btn-sm" onClick={()=>handleSubmit(false)} disabled={submitting}>
                {submitting?<span className="spinner-border spinner-border-sm"/>:<><i className="bi bi-send me-1"/>Submit</>}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// ─── AI Generate Dialog ──────────────────────────────────────────────────────

const AIGenerateDialog = ({ show, onClose, onAdd, onAlert }) => {
  const [topic, setTopic] = useState('');
  const [count, setCount] = useState(5);
  const [type, setType] = useState('mcq');
  const [difficulty, setDifficulty] = useState('medium');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (show) { setTopic(''); setCount(5); setType('mcq'); setDifficulty('medium'); setBusy(false); }
  }, [show]);

  const handleGenerate = async () => {
    if (!topic.trim()) { onAlert('Topic is required', 'warning'); return; }
    setBusy(true);
    try {
      const r = await api.post('/quiz/bank/generate',
        { topic: topic.trim(), count, type, difficulty },
        { timeout: 180000 } // AI can take up to 2 min on the server; allow 3 min wall-clock here
      );
      const payload = r.data?.data || r.data;
      const rawQs = payload?.questions || [];
      if (!rawQs.length) { onAlert('AI returned no questions — try a different prompt', 'warning'); setBusy(false); return; }

      // Normalize bank-shaped questions into assessment-question shape.
      // Reuses the same mapping as the bank picker.
      const normalized = rawQs.map(q => normalizeBankRowForAssessment({
        bankId: null,                // not saved to bank
        bankQuestionId: null,
        question:      q.question,
        type:          q.type || type,
        options:       q.options,
        correctAnswer: q.correctAnswer,
        explanation:   q.explanation,
        marks:         q.marks || 1,
        topicName:     topic,
        difficulty,
        tags:          ['ai-generated'],
        generatedByAI: true,
      }));
      // Strip bank linkage since these weren't persisted
      const clean = normalized.map(({ sourceBank, ...rest }) => rest);
      onAdd(clean);

      // Also persist to QuizBank so these questions are reusable in future assessments.
      // Non-blocking: if the bank save fails, the assessment still gets the questions.
      api.post('/quiz/bank', {
        title:         `AI: ${topic.trim()} (${new Date().toLocaleDateString('en-IN')})`,
        topicName:     topic.trim(),
        courseName:    'General',
        difficulty,
        questions:     rawQs,
        type,
        tags:          ['ai-generated'],
        generatedByAI: true,
      }).catch(() => {});

      onClose();
    } catch (e) {
      const apiMsg = e.response?.data?.message;
      const fallback = e.code === 'ECONNABORTED'
        ? 'AI is taking too long. Try fewer questions or retry.'
        : 'AI generation failed. Try again, or reduce the question count.';
      onAlert(apiMsg || fallback, 'danger');
    }
    setBusy(false);
  };

  return (
    <BSModal show={show} onClose={onClose} title="Generate Questions with AI"
      footer={<>
        <button className="btn btn-secondary" onClick={onClose} disabled={busy}>Cancel</button>
        <button className="btn btn-primary" onClick={handleGenerate} disabled={busy || !topic.trim()}>
          {busy
            ? <><span className="spinner-border spinner-border-sm me-1" />Generating… (this may take 20–30s)</>
            : <><i className="bi bi-stars me-1" />Generate {count} Question{count !== 1 ? 's' : ''}</>}
        </button>
      </>}>
      <div className="alert alert-info small">
        <i className="bi bi-lightbulb me-1" />
        AI-generated questions are added directly to this assessment. They're tagged <code>ai-generated</code> for tracking.
      </div>
      <div className="row g-2">
        <div className="col-12">
          <label className="form-label small">Topic / Prompt <span className="text-danger">*</span></label>
          <input className="form-control" value={topic} onChange={e => setTopic(e.target.value)}
                 placeholder="e.g. JavaScript promises, SQL joins, React hooks…" autoFocus />
        </div>
        <div className="col-md-4">
          <label className="form-label small">Count</label>
          <input type="number" min="1" max="30" className="form-control form-control-sm"
                 value={count} onChange={e => setCount(Math.max(1, Math.min(30, parseInt(e.target.value) || 5)))} />
        </div>
        <div className="col-md-4">
          <label className="form-label small">Type</label>
          <select className="form-select form-select-sm" value={type} onChange={e => setType(e.target.value)}>
            <option value="mcq">MCQ (single correct)</option>
            <option value="fill_blank">Fill in the blank</option>
            <option value="coding">Coding</option>
          </select>
        </div>
        <div className="col-md-4">
          <label className="form-label small">Difficulty</label>
          <select className="form-select form-select-sm" value={difficulty} onChange={e => setDifficulty(e.target.value)}>
            <option value="easy">Easy</option>
            <option value="medium">Medium</option>
            <option value="hard">Hard</option>
          </select>
        </div>
      </div>
    </BSModal>
  );
};

// ─── ChipMultiSelect ──────────────────────────────────────────────────────────
// Compact picker: selected items as removable chips + type-to-search suggestion
// list. Controlled by parent — pass `options: [{value, label}]` and the array
// of currently-selected values.

const ChipMultiSelect = ({ options = [], value = [], onChange, placeholder = 'Add…', maxChipsShown = 6 }) => {
  const [q, setQ] = useState('');
  const [open, setOpen] = useState(false);

  const selectedSet = new Set((value || []).map(String));
  const selectedOpts = options.filter(o => selectedSet.has(String(o.value)));
  const filtered = (q ? options.filter(o => !selectedSet.has(String(o.value)) && (o.label || '').toLowerCase().includes(q.toLowerCase())) : options.filter(o => !selectedSet.has(String(o.value)))).slice(0, 40);

  const add = (val) => {
    onChange([...(value || []), val]);
    setQ(''); setOpen(false);
  };
  const remove = (val) => onChange((value || []).filter(v => String(v) !== String(val)));

  return (
    <div>
      <div className="border rounded p-1 bg-white" style={{ minHeight: 38 }}>
        <div className="d-flex flex-wrap gap-1 align-items-center">
          {selectedOpts.slice(0, maxChipsShown).map(o => (
            <span key={o.value} className="badge bg-primary-subtle text-primary border border-primary-subtle" style={{ fontSize: 12 }}>
              {o.label}
              <button type="button" className="btn-close btn-close-sm ms-1" style={{ fontSize: 10 }}
                      onClick={() => remove(o.value)} aria-label="remove" />
            </span>
          ))}
          {selectedOpts.length > maxChipsShown && (
            <span className="badge bg-light text-muted border">+{selectedOpts.length - maxChipsShown} more</span>
          )}
          <div style={{ position: 'relative', flex: 1, minWidth: 120 }}>
            <input
              className="form-control form-control-sm border-0 shadow-none"
              placeholder={placeholder}
              value={q}
              onFocus={() => setOpen(true)}
              onChange={e => { setQ(e.target.value); setOpen(true); }}
              onBlur={() => setTimeout(() => setOpen(false), 150)}
              style={{ outline: 'none' }}
            />
            {open && filtered.length > 0 && (
              <div className="border rounded bg-white shadow-sm" style={{
                position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 2000,
                maxHeight: 220, overflowY: 'auto',
              }}>
                {filtered.map(o => (
                  <div key={o.value}
                       className="px-2 py-1 small"
                       style={{ cursor: 'pointer' }}
                       onMouseDown={(e) => { e.preventDefault(); add(o.value); }}>
                    {o.label}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// ─── Question-Bank Picker ─────────────────────────────────────────────────────
// Search the QuizBank across the tenant with filters and multi-select into the
// current assessment's questions array. Preserves sourceBank linkage for
// analytics (QuizBank.usageCount).

const BANK_TYPE_TO_ASSESSMENT = {
  mcq: 'mcq_single',
  fill_blank: 'short_answer',
  coding: 'coding',
};

function normalizeBankRowForAssessment(row) {
  const mappedType = BANK_TYPE_TO_ASSESSMENT[row.type] || row.type || 'mcq_single';

  // Convert parallel options + correctAnswer → structured options[].
  // AI/bank can return options as plain strings ["A", "B"] OR as objects
  // [{text:"A", isCorrect:true}, ...] — extract text robustly from either shape
  // and drop empty entries so the backend `text: required` check doesn't fire.
  let options = [];
  let correctAnswers = [];
  if (Array.isArray(row.options)) {
    const correctText = row.correctAnswer != null ? String(row.correctAnswer) : null;
    options = row.options
      .map((opt, i) => {
        let text = '';
        let isCorrect = false;
        if (opt == null) {
          // null/undefined → empty, will be filtered below
        } else if (typeof opt === 'string' || typeof opt === 'number') {
          text = String(opt).trim();
        } else if (typeof opt === 'object') {
          // Common shapes: {text}, {value}, {label}, {option}, {answer}
          const cand = opt.text ?? opt.value ?? opt.label ?? opt.option ?? opt.answer ?? '';
          text = String(cand).trim();
          if (typeof opt.isCorrect === 'boolean') isCorrect = opt.isCorrect;
        }
        if (correctText !== null && !isCorrect) {
          isCorrect = (text === correctText) || (String(i) === correctText);
        }
        return { text, isCorrect };
      })
      .filter(o => o.text.length > 0);
    correctAnswers = options.filter(o => o.isCorrect).map(o => o.text);
  }

  return {
    question:       row.question,
    type:           mappedType,
    marks:          row.marks || 1,
    options,
    correctAnswers,
    expectedAnswer: mappedType === 'short_answer' ? row.correctAnswer : '',
    explanation:    row.explanation || '',
    difficulty:     row.difficulty || 'medium',
    topic:          row.topicName || '',
    tags:           row.tags || [],
    sourceBank:     { bankId: row.bankId, bankQuestionId: row.bankQuestionId },
  };
}

const QuestionBankPicker = ({ show, onClose, onAdd, onAlert }) => {
  // Two views: 'groups' = list of quiz banks/generator quizzes,
  //            'questions' = drill-in to one group's questions
  const [view, setView] = useState('groups');

  // Groups view state
  const [groups, setGroups] = useState([]);
  const [groupsLoading, setGroupsLoading] = useState(false);
  const [groupsPage, setGroupsPage] = useState(1);
  const [groupsTotalPages, setGroupsTotalPages] = useState(1);
  const [groupFilters, setGroupFilters] = useState({ search: '', source: '', difficulty: '' });
  const [selectedGroup, setSelectedGroup] = useState(null);

  // Questions view state
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [selected, setSelected] = useState(new Set());
  const [qFilters, setQFilters] = useState({ type: '', search: '' });

  const loadGroups = useCallback(async () => {
    setGroupsLoading(true);
    try {
      const params = { page: groupsPage, limit: 50, ...Object.fromEntries(Object.entries(groupFilters).filter(([, v]) => v)) };
      const r = await api.get('/training/assessments/question-bank/groups', { params });
      setGroups(r.data?.records || []);
      setGroupsTotalPages(r.data?.totalPages || 1);
    } catch (e) {
      onAlert?.(e.response?.data?.message || 'Failed to load groups', 'danger');
    }
    setGroupsLoading(false);
  }, [groupsPage, groupFilters]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadQuestions = useCallback(async () => {
    if (!selectedGroup) return;
    setLoading(true);
    try {
      const params = {
        page, limit: 50,
        bankId: selectedGroup.bankId,
        sourceCollection: selectedGroup.sourceCollection,
        ...Object.fromEntries(Object.entries(qFilters).filter(([, v]) => v)),
      };
      const r = await api.get('/training/assessments/question-bank', { params });
      setRows(r.data?.records || []);
      setTotalPages(r.data?.totalPages || 1);
    } catch (e) {
      onAlert?.(e.response?.data?.message || 'Failed to load questions', 'danger');
    }
    setLoading(false);
  }, [page, qFilters, selectedGroup]); // eslint-disable-line react-hooks/exhaustive-deps

  // Reset on open
  useEffect(() => {
    if (show) {
      setView('groups');
      setSelectedGroup(null);
      setSelected(new Set());
      setRows([]);
      setPage(1);
      setGroupsPage(1);
      setGroupFilters({ search: '', source: '', difficulty: '' });
      setQFilters({ type: '', search: '' });
    }
  }, [show]);

  useEffect(() => { if (show && view === 'groups') loadGroups(); }, [show, view, loadGroups]);
  useEffect(() => { if (show && view === 'questions') loadQuestions(); }, [show, view, loadQuestions]);

  const openGroup = (g) => {
    setSelectedGroup(g);
    setSelected(new Set());
    setRows([]);
    setPage(1);
    setQFilters({ type: '', search: '' });
    setView('questions');
  };

  const backToGroups = () => {
    setView('groups');
    setSelectedGroup(null);
    setSelected(new Set());
  };

  const toggleRow = (row) => {
    const key = `${row.bankId}:${row.bankQuestionId}`;
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };
  const isSelected = (row) => selected.has(`${row.bankId}:${row.bankQuestionId}`);

  const toggleSelectAll = () => {
    if (selected.size === rows.length) {
      setSelected(new Set());
    } else {
      setSelected(new Set(rows.map(r => `${r.bankId}:${r.bankQuestionId}`)));
    }
  };

  const handleAdd = () => {
    const picked = rows.filter(r => selected.has(`${r.bankId}:${r.bankQuestionId}`));
    if (!picked.length) { onAlert?.('Select at least one question', 'warning'); return; }
    const normalized = picked.map(normalizeBankRowForAssessment);
    onAdd(normalized);
    onClose();
  };

  const updateGroupFilter = (k, v) => { setGroupsPage(1); setGroupFilters(f => ({ ...f, [k]: v })); };
  const updateQFilter     = (k, v) => { setPage(1);       setQFilters(f => ({ ...f, [k]: v })); };

  const titleNode = view === 'groups'
    ? 'Add Questions from Bank — Pick a Group'
    : <>
        <button className="btn btn-sm btn-outline-secondary me-2" onClick={backToGroups}>
          <i className="bi bi-arrow-left me-1" />Back
        </button>
        <span>{selectedGroup?.title || 'Group'} <span className="text-muted small">· {selectedGroup?.totalQuestions || 0} questions</span></span>
      </>;

  return (
    <BSModal show={show} onClose={onClose} title={titleNode} size="modal-xl"
      footer={view === 'questions' ? <>
        <span className="text-muted small me-auto">
          {selected.size} selected · {rows.length} shown
        </span>
        <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
        <button className="btn btn-primary" onClick={handleAdd} disabled={!selected.size}>
          <i className="bi bi-plus-lg me-1" />Add {selected.size || ''} to Assessment
        </button>
      </> : <>
        <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
      </>}>

      {view === 'groups' && (
        <>
          {/* Group filters */}
          <div className="row g-2 mb-3">
            <div className="col-md-5">
              <input className="form-control form-control-sm" placeholder="Search by title / topic / course..."
                     value={groupFilters.search} onChange={e => updateGroupFilter('search', e.target.value)} />
            </div>
            <div className="col-md-3">
              <select className="form-select form-select-sm" value={groupFilters.source}
                      onChange={e => updateGroupFilter('source', e.target.value)}>
                <option value="">Any source</option>
                <option value="ai">AI-Generated</option>
                <option value="manual">Manual</option>
              </select>
            </div>
            <div className="col-md-3">
              <select className="form-select form-select-sm" value={groupFilters.difficulty}
                      onChange={e => updateGroupFilter('difficulty', e.target.value)}>
                <option value="">Any difficulty</option>
                <option value="easy">Easy</option>
                <option value="medium">Medium</option>
                <option value="hard">Hard</option>
              </select>
            </div>
          </div>

          {groupsLoading ? (
            <div className="text-center py-4"><div className="spinner-border text-primary" /></div>
          ) : groups.length === 0 ? (
            <div className="text-center py-4 text-muted">
              <i className="bi bi-inbox fs-3 d-block mb-2" />
              No quiz groups found. Create some via <strong>Quiz Bank</strong> or <strong>Quiz Generator</strong> first.
            </div>
          ) : (
            <div className="row g-2" style={{ maxHeight: 460, overflowY: 'auto' }}>
              {groups.map(g => (
                <div className="col-md-6" key={`${g.sourceCollection}:${g.bankId}`}>
                  <div className="card h-100 shadow-sm" style={{ cursor: 'pointer' }} onClick={() => openGroup(g)}>
                    <div className="card-body">
                      <div className="d-flex align-items-start mb-2">
                        <div className="flex-grow-1">
                          <h6 className="mb-1">{g.title}</h6>
                          <div className="small text-muted">
                            {g.courseName ? <CourseNameBadge courseName={g.courseName} courseColor={g.courseColor} compact className="me-2" /> : null}
                            {g.topicName && g.topicName !== g.title ? <span className="me-2">· {g.topicName}</span> : null}
                          </div>
                        </div>
                        <span className="badge bg-primary-subtle text-primary border">
                          {g.totalQuestions} Q
                        </span>
                      </div>
                      <div className="d-flex gap-1 flex-wrap small">
                        <span className={`badge bg-${g.difficulty === 'easy' ? 'success' : g.difficulty === 'hard' ? 'danger' : g.difficulty === 'mixed' ? 'secondary' : 'warning'}-subtle text-dark border`}>
                          {g.difficulty || '—'}
                        </span>
                        {g.generatedByAI
                          ? <span className="badge bg-info-subtle text-info border"><i className="bi bi-robot me-1" />AI</span>
                          : <span className="badge bg-light text-muted border">Manual</span>}
                        <span className="badge bg-light text-muted border">
                          {g.sourceCollection === 'quiz_generator' ? 'Quiz Generator' : 'Quiz Bank'}
                        </span>
                        {(g.tags || []).slice(0, 3).map((t, i) => (
                          <span key={i} className="badge bg-light text-muted border">{t}</span>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {groupsTotalPages > 1 && (
            <div className="d-flex justify-content-center mt-2">
              <div className="btn-group btn-group-sm">
                <button className="btn btn-outline-secondary" disabled={groupsPage === 1} onClick={() => setGroupsPage(p => p - 1)}>Prev</button>
                <span className="btn btn-light disabled">Page {groupsPage} / {groupsTotalPages}</span>
                <button className="btn btn-outline-secondary" disabled={groupsPage >= groupsTotalPages} onClick={() => setGroupsPage(p => p + 1)}>Next</button>
              </div>
            </div>
          )}
        </>
      )}

      {view === 'questions' && (
        <>
          {/* Per-group question filters */}
          <div className="row g-2 mb-3">
            <div className="col-md-4">
              <select className="form-select form-select-sm" value={qFilters.type}
                      onChange={e => updateQFilter('type', e.target.value)}>
                <option value="">Any type</option>
                <option value="mcq">MCQ</option>
                <option value="fill_blank">Fill Blank</option>
                <option value="coding">Coding</option>
              </select>
            </div>
            <div className="col-md-8">
              <input className="form-control form-control-sm" placeholder="Search question text..."
                     value={qFilters.search} onChange={e => updateQFilter('search', e.target.value)} />
            </div>
          </div>

          {loading ? (
            <div className="text-center py-4"><div className="spinner-border text-primary" /></div>
          ) : rows.length === 0 ? (
            <div className="text-center py-4 text-muted">
              <i className="bi bi-inbox fs-3 d-block mb-2" />
              No questions match the filter.
            </div>
          ) : (
            <div className="table-responsive" style={{ maxHeight: 420 }}>
              <table className="table table-sm table-hover align-middle">
                <thead className="table-light sticky-top">
                  <tr>
                    <th style={{ width: 36 }}>
                      <input type="checkbox"
                             checked={rows.length > 0 && selected.size === rows.length}
                             ref={el => { if (el) el.indeterminate = selected.size > 0 && selected.size < rows.length; }}
                             onChange={toggleSelectAll}
                             title="Select all on this page" />
                    </th>
                    <th>Question</th>
                    <th style={{ width: 120 }}>Type</th>
                    <th style={{ width: 100 }}>Difficulty</th>
                    <th style={{ width: 70 }}>Marks</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map(r => {
                    const key = `${r.bankId}:${r.bankQuestionId}`;
                    return (
                      <tr key={key} className={isSelected(r) ? 'table-primary' : ''} style={{ cursor: 'pointer' }} onClick={() => toggleRow(r)}>
                        <td><input type="checkbox" checked={isSelected(r)} onChange={() => toggleRow(r)} onClick={e => e.stopPropagation()} /></td>
                        <td className="small">{r.question?.substring(0, 140)}{r.question?.length > 140 ? '…' : ''}</td>
                        <td><span className="badge bg-light text-dark border small">{r.type}</span></td>
                        <td><span className={`badge bg-${r.difficulty === 'easy' ? 'success' : r.difficulty === 'hard' ? 'danger' : 'warning'}-subtle text-dark border small`}>{r.difficulty || '—'}</span></td>
                        <td>{r.marks || 1}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {totalPages > 1 && (
            <div className="d-flex justify-content-center mt-2">
              <div className="btn-group btn-group-sm">
                <button className="btn btn-outline-secondary" disabled={page === 1} onClick={() => setPage(p => p - 1)}>Prev</button>
                <span className="btn btn-light disabled">Page {page} / {totalPages}</span>
                <button className="btn btn-outline-secondary" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}>Next</button>
              </div>
            </div>
          )}
        </>
      )}
    </BSModal>
  );
};

// ─── Main Component ─────────────────────────────────────────────────────────────

const blankForm = {
  title:'', description:'', instructions:'',
  courseId:'', batchId:'',
  assignedBatches: [], assignedStudents: [],
  type:'quiz', difficulty:'mixed', tags:[],
  questions:[], totalMarks:0, passingMarks:0, duration:60,
  startDate:'', endDate:'',
  isPublished:false, allowReview:true, shuffleQ:false,
  attemptMode:'open_book', attemptLimit:1, negativeMarking:0,
};

const getStudentSubmissionState = (assessment) => {
  const submission = assessment.mySubmission;
  if (!submission) return assessment.status === 'closed' ? 'missed' : 'todo';
  if (submission.status === 'in_progress') return 'in_progress';
  if (submission.status === 'evaluated') return submission.passed === false ? 'needs_review' : 'completed';
  return 'submitted';
};

const STUDENT_STATE_META = {
  todo: { label: 'Ready to start', icon: 'bi-play-circle', tone: 'primary' },
  in_progress: { label: 'In progress', icon: 'bi-hourglass-split', tone: 'warning' },
  submitted: { label: 'Submitted', icon: 'bi-send-check', tone: 'info' },
  completed: { label: 'Completed', icon: 'bi-check-circle', tone: 'success' },
  needs_review: { label: 'Completed', icon: 'bi-clipboard-data', tone: 'danger' },
  missed: { label: 'Closed', icon: 'bi-lock', tone: 'secondary' },
};

function StudentAssignedAssessments({
  assessments,
  loading,
  page,
  setPage,
  totalPages,
  onStart,
  onResult,
}) {
  const readyCount = assessments.filter((item) => ['todo', 'in_progress'].includes(getStudentSubmissionState(item))).length;
  const submittedCount = assessments.filter((item) => ['submitted', 'completed', 'needs_review'].includes(getStudentSubmissionState(item))).length;
  const completedCount = assessments.filter((item) => getStudentSubmissionState(item) === 'completed').length;

  return (
    <main className="student-assessments-page">
      <header className="student-assessments-header">
        <div className="student-assessments-header-copy">
          <span className="student-assessments-eyebrow"><i className="bi bi-clipboard2-check" /> Learning checks</span>
          <h1>My Assessments</h1>
          <p>View and complete the assessments assigned to you.</p>
        </div>
        <div className="student-assessments-header-art" aria-hidden="true">
          <i className="bi bi-patch-check" />
        </div>
      </header>

      <section className="student-assessments-summary" aria-label="Assessment summary">
        <div><i className="bi bi-collection" /><span><strong>{assessments.length}</strong>Assigned</span></div>
        <div><i className="bi bi-lightning-charge" /><span><strong>{readyCount}</strong>To complete</span></div>
        <div><i className="bi bi-send-check" /><span><strong>{submittedCount}</strong>Submitted</span></div>
        <div><i className="bi bi-award" /><span><strong>{completedCount}</strong>Passed</span></div>
      </section>

      <div className="student-assessments-section-heading">
        <div><span>ASSIGNED TO YOU</span><h2>Available assessments</h2></div>
        <span>{assessments.length} assigned</span>
      </div>

      {loading ? (
        <div className="student-assessments-loading"><span className="spinner-border spinner-border-sm" /> Loading assessments...</div>
      ) : assessments.length > 0 ? (
        <section className="student-assessments-grid">
          {assessments.map((assessment) => {
            const state = getStudentSubmissionState(assessment);
            const meta = STUDENT_STATE_META[state];
            const courseName = assessment.course?.courseName || assessment.course?.name || 'General assessment';
            const dueDate = assessment.endDate || assessment.dueDate;
            const canTake = assessment.status === 'active' && ['todo', 'in_progress'].includes(state);
            const canViewResult = assessment.mySubmission && !canTake;
            return (
              <article className="student-assessment-card" key={assessment._id}>
                <div className="student-assessment-card-accent" />
                <div className="student-assessment-card-top">
                  <span className={`student-assessment-status is-${meta.tone}`}><i className={`bi ${meta.icon}`} />{meta.label}</span>
                  <span className="student-assessment-type">{String(assessment.type || 'assessment').replaceAll('_', ' ')}</span>
                </div>
                <div className="student-assessment-course"><i className="bi bi-journal-code" />{courseName}</div>
                <h3>{assessment.title}</h3>
                {assessment.description && <p className="student-assessment-description">{assessment.description}</p>}
                <div className="student-assessment-details">
                  <span><i className="bi bi-clock" /><b>{assessment.duration > 0 ? `${assessment.duration} min` : 'No limit'}</b><small>Duration</small></span>
                  <span><i className="bi bi-ui-checks-grid" /><b>{assessment.questions?.length || 0}</b><small>Questions</small></span>
                  <span><i className="bi bi-trophy" /><b>{assessment.totalMarks || 0}</b><small>Total marks</small></span>
                  <span><i className="bi bi-calendar-event" /><b>{dueDate ? fmtDate(dueDate) : 'Open'}</b><small>Due date</small></span>
                </div>
                <div className="student-assessment-card-footer">
                  <div className="student-assessment-attempts">
                    <i className="bi bi-arrow-repeat" />
                    <span>{assessment.attemptLimit || 1} {(assessment.attemptLimit || 1) === 1 ? 'attempt' : 'attempts'}</span>
                  </div>
                  {canTake && (
                    <button type="button" className="student-assessment-primary-action" onClick={() => onStart(assessment)}>
                      {state === 'in_progress' ? 'Resume' : 'Start assessment'} <i className="bi bi-arrow-right" />
                    </button>
                  )}
                  {canViewResult && (
                    <button type="button" className="student-assessment-secondary-action" onClick={() => onResult(assessment, assessment.mySubmission)}>
                      View result <i className="bi bi-arrow-right" />
                    </button>
                  )}
                  {!canTake && !canViewResult && <span className="student-assessment-closed"><i className="bi bi-lock" /> Closed</span>}
                </div>
              </article>
            );
          })}
        </section>
      ) : null}

      <ScholarshipQuizPrompt />

      {totalPages > 1 && (
        <nav className="student-assessments-pagination" aria-label="Assessment pages">
          <button type="button" disabled={page <= 1} onClick={() => setPage((value) => Math.max(1, value - 1))}><i className="bi bi-chevron-left" /></button>
          <span>Page {page} of {totalPages}</span>
          <button type="button" disabled={page >= totalPages} onClick={() => setPage((value) => Math.min(totalPages, value + 1))}><i className="bi bi-chevron-right" /></button>
        </nav>
      )}
    </main>
  );
}

export default function Assessments() {
  const { user, tenant } = useAuth();
  const role = user?.roleKey || user?.role || '';
  // Faculty / Teacher / Trainer can also create assessments — the backend
  // route `POST /training/assessments` already permits these roles. Keeping
  // the frontend gate stricter than the API just hides the "Create" button
  // and confuses faculty into thinking they can't make assessments.
  const canWrite = ['admin','manager','team_lead','faculty','teacher','trainer'].includes(role);
  const canAdmin = ['admin'].includes(role);
  const isFaculty = ['faculty','teacher','trainer'].includes(role);
  const isStudent = role === 'student';

  // Detect education tenant so we can swap "Course/Batch" terminology for
  // "Subject/Class". `industryId` may be populated (object) or raw — coerce
  // defensively.
  const industrySlug = (() => {
    const t = tenant; if (!t) return '';
    const refs = [t.industryId, t.industry, t.industrySlug];
    for (const ref of refs) {
      if (typeof ref === 'string' && ref) return ref.toLowerCase();
      if (ref && typeof ref === 'object') {
        if (typeof ref.slug === 'string') return ref.slug.toLowerCase();
        if (typeof ref.name === 'string') return ref.name.toLowerCase();
      }
    }
    return '';
  })();
  const isEdu = industrySlug === 'education' || industrySlug === 'school';
  // Centralised label dictionary so all `T.x` accesses stay in sync.
  const T = isEdu ? {
    course: 'Subject', courses: 'Subjects', allCourses: 'All Subjects',
    batch: 'Class', batches: 'Classes', primaryBatch: 'Primary Class',
    additionalBatches: 'Additional Classes', searchBatches: 'Type to search classes…',
    overridesBatches: '(overrides classes if set)',
  } : {
    course: 'Course', courses: 'Courses', allCourses: 'All Courses',
    batch: 'Batch', batches: 'Batches', primaryBatch: 'Primary Batch',
    additionalBatches: 'Additional Batches', searchBatches: 'Type to search batches…',
    overridesBatches: '(overrides batches if set)',
  };

  const [assessments, setAssessments] = useState([]);
  const [courses, setCourses]         = useState([]);
  const [batches, setBatches]         = useState([]);
  const [students, setStudents]       = useState([]);
  const [stats, setStats]             = useState(null);
  const [loading, setLoading]         = useState(false);

  const [showForm, setShowForm]       = useState(false);
  const [editTarget, setEditTarget]   = useState(null);
  const [form, setForm]               = useState(blankForm);
  const [saving, setSaving]           = useState(false);

  const [filterStatus, setFilterStatus] = useState('');
  const [filterType,   setFilterType]   = useState('');
  const [search,       setSearch]       = useState('');
  const [page, setPage]                 = useState(1);
  const [totalPages, setTotalPages]     = useState(1);

  const [dlgSubs, setDlgSubs]       = useState(null);
  const [takeAssessment, setTakeAssessment] = useState(null);
  const [alert, setAlert]           = useState({ show:false, msg:'', type:'success' });
  const showAlert = (msg, type='success') => setAlert({ show:true, msg, type });

  const [showBankPicker, setShowBankPicker]   = useState(false);
  const [showAIDialog, setShowAIDialog]       = useState(false);
  const [showUploadDialog, setShowUploadDialog] = useState(false);

  // ── Load reference data ─────────────────────────────────────────────────────

  useEffect(() => {
    if (isEdu) {
      // Education tenants: hydrate the "Course" and "Batch" dropdowns from
      // School Configuration so admins can pick a Subject + Class without
      // first creating training-flavored Course/Batch records. Each entry
      // gets a synthetic _id so the existing UI/save path keeps working —
      // the saved value is the string label, not a real ObjectId, but the
      // assessment route accepts that shape too.
      api.get('/records/school_config', { params: { limit: 1 } })
        .then(r => {
          const rd = (r.data?.records || [])[0]?.recordData || {};
          const subjects = Array.isArray(rd.subjects) ? rd.subjects : [];
          const classes  = Array.isArray(rd.classes)  ? rd.classes  : [];
          const sections = Array.isArray(rd.sections) ? rd.sections : [];
          // Subjects → "course" options
          setCourses(subjects.map(s => ({ _id: `sub_${s}`, name: s, courseName: s })));
          // Classes × sections → "batch" options. If no sections, just classes.
          const combos = sections.length
            ? classes.flatMap(c => sections.map(sec => ({
                _id: `cls_${c}_${sec}`, batchNumber: `${c} — ${sec}`,
              })))
            : classes.map(c => ({ _id: `cls_${c}`, batchNumber: c }));
          setBatches(combos);
        })
        .catch(() => {});
    } else {
      api.get('/training/courses', { params: { limit:200 } }).then(r => setCourses(r.data?.data||r.data?.records||[])).catch(()=>{});
      api.get('/training/batches', { params: { limit:200 } }).then(r => setBatches(r.data?.records||r.data?.data||[])).catch(()=>{});
    }
    if (canWrite) {
      api.get('/training/assessments/stats').then(r => setStats(r.data?.data||r.data)).catch(()=>{});
      // Use the dedicated `eligible-students` endpoint instead of `/users` so
      // faculty / teacher / trainer (who don't have `users:view` permission)
      // can still populate the assigned-students picker. The endpoint scopes
      // the result to students in the requester's allocated batches.
      api.get('/training/assessments/eligible-students')
        .then(r => setStudents(r.data?.data || r.data?.records || r.data || []))
        .catch(() => setStudents([]));
    }
  }, [canWrite]);

  // ── Fetch ───────────────────────────────────────────────────────────────────

  const fetch = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit:20, search:search||undefined, status:filterStatus||undefined, type:filterType||undefined };
      const r = await api.get('/training/assessments', { params });
      setAssessments(r.data?.records||r.data?.data||[]);
      setTotalPages(r.data?.totalPages||1);
    } catch(_) {}
    setLoading(false);
  }, [page, search, filterStatus, filterType]);

  useEffect(() => { fetch(); }, [fetch]);

  const setF = (k,v) => setForm(p=>({...p,[k]:v}));

  const openCreate = () => { setForm({ ...blankForm, questions: [] }); setEditTarget(null); setShowForm(true); };
  const openEdit   = (a) => {
    setForm({
      title:            a.title,
      description:      a.description || '',
      instructions:     a.instructions || '',
      courseId:         a.course?._id || '',
      batchId:          a.batch?._id || '',
      assignedBatches:  (a.assignedBatches || []).map(b => b?._id || b),
      assignedStudents: (a.assignedStudents || []).map(s => s?._id || s),
      type:             a.type || 'quiz',
      difficulty:       a.difficulty || 'mixed',
      tags:             a.tags || [],
      questions:        a.questions || [],
      totalMarks:       a.totalMarks || 0,
      passingMarks:     a.passingMarks || 0,
      duration:         a.duration || 60,
      startDate:        a.startDate ? new Date(a.startDate).toISOString().split('T')[0] : '',
      endDate:          (a.endDate || a.dueDate) ? new Date(a.endDate || a.dueDate).toISOString().split('T')[0] : '',
      isPublished:      !!a.isPublished,
      allowReview:      a.allowReview !== false,
      shuffleQ:         !!a.shuffleQ,
      attemptMode:      a.attemptMode || 'open_book',
      attemptLimit:     a.attemptLimit ?? 1,
      negativeMarking:  a.negativeMarkingPerWrong ?? a.negativeMarking ?? 0,
    });
    setEditTarget(a);
    setShowForm(true);
  };

  const handleSave = async () => {
    if (!form.title.trim()) { showAlert('Title is required.','danger'); return; }

    // Pre-flight: catch malformed questions before the backend rejects with 422
    const MCQ_TYPES = ['mcq_single', 'mcq_multi', 'true_false'];
    for (let i = 0; i < form.questions.length; i++) {
      const q = form.questions[i];
      if (!q.question || !String(q.question).trim()) {
        showAlert(`Question ${i + 1}: question text is empty.`, 'danger'); return;
      }
      if (MCQ_TYPES.includes(q.type)) {
        const opts = (q.options || []).filter(o => o && String(o.text || '').trim());
        if (opts.length < 2) {
          showAlert(`Question ${i + 1}: needs at least 2 non-empty options.`, 'danger'); return;
        }
        if (!opts.some(o => o.isCorrect)) {
          showAlert(`Question ${i + 1}: mark at least one correct option.`, 'danger'); return;
        }
      }
    }

    setSaving(true);
    try {
      // Strip client-only _id fields from questions so Mongoose generates fresh ones.
      // Also drop options with empty text so backend's `text: required` doesn't fire.
      const cleanQuestions = form.questions.map(q => {
        const { _id, order, ...rest } = q;
        if (Array.isArray(rest.options)) {
          rest.options = rest.options.filter(o => o && String(o.text || '').trim());
        }
        return rest;
      });
      // Education tenants pick from synthetic Subject/Class options that
      // don't have real ObjectIds. Strip those before save so Mongoose
      // doesn't reject them, and store the picked labels in `tags` for
      // display + filtering.
      const isSyntheticCourse = form.courseId && /^sub_/i.test(form.courseId);
      const isSyntheticBatch  = form.batchId  && /^cls_/i.test(form.batchId);
      const subjectLabel = isSyntheticCourse
        ? (courses.find(c => c._id === form.courseId)?.name || courses.find(c => c._id === form.courseId)?.courseName || '')
        : '';
      const classLabel   = isSyntheticBatch
        ? (batches.find(b => b._id === form.batchId)?.batchNumber || '')
        : '';
      const extraTags = [...(form.tags || [])];
      if (subjectLabel) extraTags.push(`subject:${subjectLabel}`);
      if (classLabel)   extraTags.push(`class:${classLabel}`);

      const payload = {
        ...form,
        // Drop synthetic IDs — backend would reject them as CastError.
        courseId: isSyntheticCourse ? undefined : form.courseId,
        batchId:  isSyntheticBatch  ? undefined : form.batchId,
        // Education-tenant labels live on tags for display.
        tags:     extraTags.length ? Array.from(new Set(extraTags)) : (form.tags || []),
        // Also surface as separate fields if backend ever wants to use them.
        subjectName: subjectLabel || form.subjectName,
        className:   classLabel   || form.className,
        questions:  cleanQuestions,
        totalMarks: cleanQuestions.reduce((s,q)=>s+(Number(q.marks)||0),0),
      };
      let r;
      if (editTarget) r = await api.put(`/training/assessments/${editTarget._id}`, payload);
      else            r = await api.post('/training/assessments', payload);
      if (r.data?.success !== false) {
        showAlert(editTarget ? 'Updated.' : 'Created.', 'success');
        setShowForm(false);
        fetch();
        api.get('/training/assessments/stats').then(r => setStats(r.data?.data||r.data)).catch(()=>{});
      } else showAlert(r.data?.message||'Failed.','danger');
    } catch(e) { showAlert(e.response?.data?.message||'Failed.','danger'); }
    setSaving(false);
  };

  const handlePublish = async (a) => {
    try {
      await api.post(`/training/assessments/${a._id}/publish`, {});
      showAlert('Published.','success');
      fetch();
    } catch(e) { showAlert(e.response?.data?.message||'Failed.','danger'); }
  };

  const handleClose = async (a) => {
    try {
      await api.post(`/training/assessments/${a._id}/close`, {});
      showAlert('Closed.','success');
      fetch();
    } catch(e) { showAlert(e.response?.data?.message||'Failed.','danger'); }
  };

  // View the student's own submission result. Re-fetches the latest submission
  // from the server so the grade is always up-to-date with the teacher's evaluation.
  const [viewResult, setViewResult] = useState(null);
  const openResult = async (a, initialSub) => {
    try {
      const r = await api.get(`/training/assessments/${a._id}`);
      const full = r.data?.data || r.data;
      const sub = (full?.submissions || []).find(s => (s.student?._id || s.student)?.toString() === user?.id || s.student?.toString() === user?.id);
      const subToShow = sub || initialSub;
      if (!subToShow) { showAlert('No submission found for you.', 'warning'); return; }
      setViewResult({ assessment: full || a, submission: subToShow });
    } catch (e) {
      // Fall back to the cached data if fetch fails
      setViewResult({ assessment: a, submission: initialSub });
    }
  };

  const handleDelete = async (a) => {
    if (!window.confirm(`Delete "${a.title}"?`)) return;
    try {
      await api.delete(`/training/assessments/${a._id}`);
      showAlert('Deleted.','success');
      fetch();
    } catch(e) { showAlert(e.response?.data?.message||'Failed.','danger'); }
  };

  const handleStartAssessment = async (assessment) => {
    try {
      await api.post(`/training/assessments/${assessment._id}/start`);
      setTakeAssessment(assessment);
    } catch(e) {
      showAlert(e.response?.data?.message || 'Failed to start assessment.', 'danger');
    }
  };

  const handleSubmitAssessment = async (answerArr, violations) => {
    const r = await api.post(`/training/assessments/${takeAssessment._id}/submit`, { answers: answerArr, violations });
    showAlert('Assessment submitted!', 'success');
    fetch();
    return r.data?.data || r.data || {};
  };

  const filteredBatches = form.courseId ? batches.filter(b => b.course?._id===form.courseId||b.courseId===form.courseId) : batches;

  if (isStudent) {
    return (
      <div className="student-assessments-shell">
        <AlertToast message={alert.msg} type={alert.type} show={alert.show} onClose={()=>setAlert(p=>({...p,show:false}))} />
        <StudentAssignedAssessments
          assessments={assessments}
          loading={loading}
          page={page}
          setPage={setPage}
          totalPages={totalPages}
          onStart={handleStartAssessment}
          onResult={openResult}
        />
        {viewResult && (
          <BSModal show={true} onClose={()=>setViewResult(null)} title={`${viewResult.assessment?.title || 'Result'} - Your Submission`} size="modal-lg">
            <StudentResultView result={viewResult} />
            <div className="d-flex justify-content-end gap-2 mt-3 pt-2 border-top">
              <button className="btn btn-secondary btn-sm" onClick={()=>setViewResult(null)}>Close</button>
            </div>
          </BSModal>
        )}
        <TakeAssessmentModal
          show={!!takeAssessment}
          assessment={takeAssessment}
          onClose={() => setTakeAssessment(null)}
          onSubmit={handleSubmitAssessment}
          onAlert={showAlert}
        />
      </div>
    );
  }

  return (
    <div className="container-fluid py-3">
      <AlertToast message={alert.msg} type={alert.type} show={alert.show} onClose={()=>setAlert(p=>({...p,show:false}))} />

      <PageHeader
        title="Assessments"
        icon="bi-clipboard-check"
        subtitle={isEdu
          ? `Tests & exams by ${T.course.toLowerCase()} and ${T.batch.toLowerCase()}`
          : 'Quizzes, exams, assignments & practicals'}
        actions={canWrite && (
          <button className="btn btn-primary btn-sm" onClick={openCreate}>
            <i className="bi bi-plus-lg me-1" />New Assessment
          </button>
        )}
      />

      {/* Stats */}
      {stats && (
        <div className="row g-2 mb-3">
          <div className="col-6 col-md-3"><StatCard title="Total"  value={stats.total}  icon="bi-clipboard"      color="primary" /></div>
          <div className="col-6 col-md-3"><StatCard title="Active" value={stats.active} icon="bi-check-circle"   color="success" /></div>
          <div className="col-6 col-md-3"><StatCard title="Draft"  value={stats.draft}  icon="bi-file-earmark"   color="secondary" /></div>
          <div className="col-6 col-md-3"><StatCard title="Closed" value={stats.closed} icon="bi-archive"        color="warning" /></div>
        </div>
      )}

      {/* Filters */}
      <div className="row g-2 mb-3">
        <div className="col-md-5">
          <div className="input-group input-group-sm">
            <span className="input-group-text"><i className="bi bi-search" /></span>
            <input className="form-control" placeholder="Search assessments…" value={search} onChange={e=>{setSearch(e.target.value);setPage(1);}} />
          </div>
        </div>
        <div className="col-md-3">
          <select className="form-select form-select-sm" value={filterStatus} onChange={e=>{setFilterStatus(e.target.value);setPage(1);}}>
            <option value="">All Statuses</option>
            <option value="draft">Draft</option>
            <option value="active">Active</option>
            <option value="closed">Closed</option>
          </select>
        </div>
        <div className="col-md-3">
          <select className="form-select form-select-sm" value={filterType} onChange={e=>{setFilterType(e.target.value);setPage(1);}}>
            <option value="">All Types</option>
            {ASSESSMENT_TYPES.map(t=><option key={t.value} value={t.value}>{t.label}</option>)}
          </select>
        </div>
      </div>

      {/* Create/Edit Form */}
      {showForm && (
        <div className="card border-primary mb-3">
          <div className="card-header bg-primary text-white d-flex justify-content-between">
            <span>{editTarget ? 'Edit Assessment' : 'New Assessment'}</span>
            <button className="btn-close btn-close-white btn-sm" onClick={()=>setShowForm(false)} />
          </div>
          <div className="card-body">
            <div className="row g-3 mb-3">
              <div className="col-md-6">
                <label className="form-label">Title <span className="text-danger">*</span></label>
                <input className="form-control form-control-sm" value={form.title} onChange={e=>setF('title',e.target.value)} />
              </div>
              <div className="col-md-3">
                <label className="form-label">Type</label>
                <select className="form-select form-select-sm" value={form.type} onChange={e=>setF('type',e.target.value)}>
                  {ASSESSMENT_TYPES.map(t=><option key={t.value} value={t.value}>{t.label}</option>)}
                </select>
              </div>
              <div className="col-md-3">
                <label className="form-label">Duration (min)</label>
                <input className="form-control form-control-sm" type="number" min="0" value={form.duration} onChange={e=>setF('duration',e.target.value)} />
              </div>
              <div className="col-md-4">
                <label className="form-label">{T.course}</label>
                <select className="form-select form-select-sm" value={form.courseId} onChange={e=>{setF('courseId',e.target.value);setF('batchId','');}}>
                  <option value="">{T.allCourses}</option>
                  {courses.map(c=><option key={c._id} value={c._id}>{c.name || c.courseName}</option>)}
                </select>
              </div>
              <div className="col-md-4">
                <label className="form-label">{T.primaryBatch}</label>
                <select className="form-select form-select-sm" value={form.batchId} onChange={e=>setF('batchId',e.target.value)}>
                  <option value="">— none —</option>
                  {filteredBatches.map(b=><option key={b._id} value={b._id}>{b.batchNumber}</option>)}
                </select>
              </div>
              <div className="col-md-6">
                <label className="form-label small">
                  {T.additionalBatches} <span className="text-muted">(also assign to these)</span>
                </label>
                <ChipMultiSelect
                  options={batches
                    .filter(b => b._id !== form.batchId)
                    .map(b => ({ value: b._id, label: b.batchNumber + (b.course?.courseName ? ` — ${b.course.courseName}` : '') }))}
                  value={form.assignedBatches}
                  onChange={(v) => setF('assignedBatches', v)}
                  placeholder={T.searchBatches}
                />
              </div>
              <div className="col-md-6">
                <label className="form-label small">
                  Specific Students <span className="text-muted">(overrides batches if set)</span>
                </label>
                <ChipMultiSelect
                  options={students.map(s => ({
                    value: s._id,
                    label: `${s.firstName || ''} ${s.lastName || ''}`.trim() + (s.email ? ` · ${s.email}` : ''),
                  }))}
                  value={form.assignedStudents}
                  onChange={(v) => setF('assignedStudents', v)}
                  placeholder="Type a student name or email…"
                />
              </div>
              <div className="col-md-3">
                <label className="form-label">Passing Marks</label>
                <input className="form-control form-control-sm" type="number" min="0" value={form.passingMarks} onChange={e=>setF('passingMarks',e.target.value)} />
              </div>
              <div className="col-md-3">
                <label className="form-label">Difficulty</label>
                <select className="form-select form-select-sm" value={form.difficulty} onChange={e=>setF('difficulty',e.target.value)}>
                  {DIFFICULTIES.map(d => <option key={d.value} value={d.value}>{d.label}</option>)}
                </select>
              </div>
              <div className="col-md-3">
                <label className="form-label">Start Date</label>
                <input className="form-control form-control-sm" type="date" value={form.startDate} onChange={e=>setF('startDate',e.target.value)} />
              </div>
              <div className="col-md-3">
                <label className="form-label">End Date</label>
                <input className="form-control form-control-sm" type="date" value={form.endDate} onChange={e=>setF('endDate',e.target.value)} />
              </div>
              <div className="col-12">
                <label className="form-label small">Tags <span className="text-muted">(comma separated)</span></label>
                <input className="form-control form-control-sm" value={(form.tags || []).join(', ')}
                       onChange={e=>setF('tags', e.target.value.split(',').map(t=>t.trim()).filter(Boolean))}
                       placeholder="e.g. javascript, loops, beginner" />
              </div>
              <div className="col-md-4 d-flex flex-column gap-2 justify-content-end">
                <div className="form-check form-switch">
                  <input className="form-check-input" type="checkbox" id="isPublished" checked={form.isPublished} onChange={e=>setF('isPublished',e.target.checked)} />
                  <label className="form-check-label" htmlFor="isPublished">Publish immediately</label>
                </div>
                <div className="form-check form-switch">
                  <input className="form-check-input" type="checkbox" id="allowReview" checked={form.allowReview} onChange={e=>setF('allowReview',e.target.checked)} />
                  <label className="form-check-label" htmlFor="allowReview">Allow answer review</label>
                </div>
                <div className="form-check form-switch">
                  <input className="form-check-input" type="checkbox" id="shuffleQ" checked={form.shuffleQ} onChange={e=>setF('shuffleQ',e.target.checked)} />
                  <label className="form-check-label" htmlFor="shuffleQ">Shuffle questions</label>
                </div>
              </div>
              <div className="col-md-4">
                <label className="form-label">Attempt Mode</label>
                <select className="form-select form-select-sm" value={form.attemptMode} onChange={e=>setF('attemptMode',e.target.value)}>
                  {ATTEMPT_MODES.map(m=><option key={m.value} value={m.value}>{m.label}</option>)}
                </select>
              </div>
              <div className="col-md-4">
                <label className="form-label">Max Attempts</label>
                <input className="form-control form-control-sm" type="number" min="1" max="5" value={form.attemptLimit} onChange={e=>setF('attemptLimit',e.target.value)} />
              </div>
              <div className="col-md-4">
                <label className="form-label">Negative Marks per Wrong MCQ</label>
                <input className="form-control form-control-sm" type="number" min="0" step="0.25" value={form.negativeMarking} onChange={e=>setF('negativeMarking',e.target.value)} />
              </div>
              <div className="col-12">
                <label className="form-label">Description</label>
                <textarea className="form-control form-control-sm" rows={2} value={form.description} onChange={e=>setF('description',e.target.value)} />
              </div>
              <div className="col-12">
                <label className="form-label">Instructions <span className="text-muted small">(shown to students before starting)</span></label>
                <textarea className="form-control form-control-sm" rows={2} value={form.instructions} onChange={e=>setF('instructions',e.target.value)}
                          placeholder="e.g. Read each question carefully. No external resources allowed." />
              </div>
            </div>

            <hr />
            <QuestionEditor
              questions={form.questions}
              onChange={q=>setF('questions',q)}
              onOpenBank={() => setShowBankPicker(true)}
              onOpenAI={() => setShowAIDialog(true)}
              onOpenUpload={() => setShowUploadDialog(true)}
            />

            <div className="d-flex justify-content-between align-items-center mt-3">
              <span className="text-muted small">Total marks: <strong>{form.questions.reduce((s,q)=>s+(Number(q.marks)||0),0)}</strong></span>
              <div className="d-flex gap-2">
                <button className="btn btn-secondary btn-sm" onClick={()=>setShowForm(false)}>Cancel</button>
                <button className="btn btn-primary btn-sm" onClick={handleSave} disabled={saving}>
                  {saving ? <><span className="spinner-border spinner-border-sm me-1"/>Saving…</> : (editTarget ? 'Save Changes' : 'Create Assessment')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Table */}
      <div className="card">
        <div className="card-body p-0">
          {loading ? (
            <div className="text-center py-5"><div className="spinner-border text-primary" /></div>
          ) : assessments.length === 0 ? (
            <div className="text-center py-5 text-muted"><i className="bi bi-clipboard fs-1 d-block mb-2"/>No assessments found.</div>
          ) : (
            <div className="table-responsive">
              <table className="table table-sm table-hover align-middle mb-0">
                <thead className="table-light">
                  <tr>
                    <th>Title</th>
                    <th>Type</th>
                    <th>{T.batch}</th>
                    <th>Marks</th>
                    <th>Duration</th>
                    <th>Dates</th>
                    <th>Status</th>
                    <th>Submissions</th>
                    <th className="text-end">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {assessments.map(a => (
                    <tr key={a._id}>
                      <td>
                        <div className="fw-semibold">{a.title}</div>
                        {a.description && <small className="text-muted">{a.description.substring(0,60)}{a.description.length>60?'…':''}</small>}
                      </td>
                      <td><span className="badge bg-light text-dark border">{a.type}</span></td>
                      <td>
                        <div>{a.batch?.batchNumber||'—'}</div>
                        <small className="text-muted">{a.course?.name||''}</small>
                      </td>
                      <td>{a.totalMarks} <span className="text-muted small">(pass {a.passingMarks})</span></td>
                      <td>{a.duration > 0 ? `${a.duration} min` : 'No limit'}</td>
                      <td>
                        <small>
                          {a.startDate ? fmtDate(a.startDate) : '—'}
                          {a.endDate && <><br/>{fmtDate(a.endDate)}</>}
                        </small>
                      </td>
                      <td>
                        <span className={`badge bg-${STATUS_COLOR[a.status]||'secondary'}`}>{a.status}</span>
                        {a.isPublished && <span className="badge bg-success-subtle text-success border border-success-subtle ms-1 small">Published</span>}
                        {a.attemptMode && a.attemptMode !== 'open_book' && (
                          <span className={`badge bg-${ATTEMPT_MODES.find(m=>m.value===a.attemptMode)?.color||'secondary'} ms-1 small`}>
                            {ATTEMPT_MODES.find(m=>m.value===a.attemptMode)?.label}
                          </span>
                        )}
                      </td>
                      <td className="text-center">{a.submissionCount ?? (a.submissions?.length ?? 0)}</td>
                      <td className="text-end" style={{whiteSpace:'nowrap'}}>
                        {isStudent && a.mySubmission && (
                          <button className="btn btn-sm btn-info me-1" title="View your result"
                            onClick={() => openResult(a, a.mySubmission)}>
                            <i className="bi bi-eye me-1"/>View Result
                          </button>
                        )}
                        {isStudent && !a.mySubmission && a.status === 'active' && (
                          <button className="btn btn-sm btn-success me-1" title="Take Assessment" onClick={()=>handleStartAssessment(a)}>
                            <i className="bi bi-play-fill me-1"/>Take
                          </button>
                        )}
                        {canWrite && (
                          <>
                            {a.status === 'draft' && (
                              <button className="btn btn-outline-success btn-sm me-1" title="Publish" onClick={()=>handlePublish(a)}>
                                <i className="bi bi-send" />
                              </button>
                            )}
                            {a.status === 'active' && (
                              <button className="btn btn-outline-warning btn-sm me-1" title="Close" onClick={()=>handleClose(a)}>
                                <i className="bi bi-archive" />
                              </button>
                            )}
                            <button className="btn btn-outline-info btn-sm me-1" title="Submissions" onClick={()=>setDlgSubs(a)}>
                              <i className="bi bi-people" />
                            </button>
                            <button className="btn btn-outline-primary btn-sm me-1" title="Edit" onClick={()=>openEdit(a)}>
                              <i className="bi bi-pencil" />
                            </button>
                          </>
                        )}
                        {canAdmin && (
                          <button className="btn btn-outline-danger btn-sm" title="Delete" onClick={()=>handleDelete(a)}>
                            <i className="bi bi-trash" />
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <nav className="mt-3 d-flex justify-content-center">
          <ul className="pagination pagination-sm mb-0">
            <li className={`page-item ${page<=1?'disabled':''}`}><button className="page-link" onClick={()=>setPage(p=>Math.max(1,p-1))}>‹</button></li>
            {Array.from({length:totalPages},(_,i)=>i+1).map(p=>(
              <li key={p} className={`page-item ${p===page?'active':''}`}><button className="page-link" onClick={()=>setPage(p)}>{p}</button></li>
            ))}
            <li className={`page-item ${page>=totalPages?'disabled':''}`}><button className="page-link" onClick={()=>setPage(p=>Math.min(totalPages,p+1))}>›</button></li>
          </ul>
        </nav>
      )}

      <SubmissionsDialog
        show={!!dlgSubs}
        assessment={dlgSubs}
        onClose={()=>setDlgSubs(null)}
        onAlert={showAlert}
        canWrite={canWrite}
      />

      {/* Student result viewer — shows their submission with grade/feedback */}
      {viewResult && (
        <BSModal show={true} onClose={()=>setViewResult(null)} title={`${viewResult.assessment?.title || 'Result'} — Your Submission`} size="modal-lg">
          <StudentResultView result={viewResult} />
          <div className="d-flex justify-content-end gap-2 mt-3 pt-2 border-top">
            <button className="btn btn-secondary btn-sm" onClick={()=>setViewResult(null)}>Close</button>
          </div>
        </BSModal>
      )}

      <TakeAssessmentModal
        show={!!takeAssessment}
        assessment={takeAssessment}
        onClose={() => setTakeAssessment(null)}
        onSubmit={handleSubmitAssessment}
        onAlert={showAlert}
      />

      <QuestionBankPicker
        show={showBankPicker}
        onClose={() => setShowBankPicker(false)}
        onAdd={(pickedQuestions) => {
          setF('questions', [...form.questions, ...pickedQuestions]);
          showAlert(`Added ${pickedQuestions.length} question(s) from bank`);
        }}
        onAlert={showAlert}
      />

      <AIGenerateDialog
        show={showAIDialog}
        onClose={() => setShowAIDialog(false)}
        onAdd={(aiQuestions) => {
          setF('questions', [...form.questions, ...aiQuestions]);
          showAlert(`Added ${aiQuestions.length} AI-generated question(s)`);
        }}
        onAlert={showAlert}
      />

      <UploadQuestionsDialog
        show={showUploadDialog}
        onClose={() => setShowUploadDialog(false)}
        onAdd={(uploadedQuestions) => {
          setF('questions', [...form.questions, ...uploadedQuestions]);
          showAlert(`Added ${uploadedQuestions.length} question(s) from file`);
        }}
        onAlert={showAlert}
      />
    </div>
  );
}
